import { CONFIG_EVENTS, UPDATE_KEYS, PERFORMANCE_DEFAULTS, PERFORMANCE_ENV_KEYS, CacheEntry } from '../constants';
import type { FrontendConfiguration, LabelParams, ThemeModeColors } from '../types/configuration.types';
import { sanitizeText } from '../utils/sanitizer';
import logger from './loggerService';
import { NAV_CACHE_CONFIG } from '../config/navigationConfig';


/**
 * Default UI labels used as fallback when backend configuration is unavailable.
 * Matches backend DEFAULT_LABELS for parity.
 * @security Production-ready defaults.
 */
const DEFAULT_LABELS: Record<string, string> = {
  // Admin Notifications Page
  'admin_notifications_page_title': 'System Notifications',
  'admin_notifications_title': 'System Notifications',
  'admin_notifications_subtitle': 'Global diagnostic alerts, system anomalies, and governance approvals.',
  'admin_notifications_unread': 'Unread',
  'admin_notifications_mark_all_read': 'Mark All Read',
  'admin_notifications_empty_title': 'Telemetry Clear',
  'admin_notifications_empty_desc': 'No system alerts or governance notifications at this time.',
  'admin_notifications_col_type': 'Type',
  'admin_notifications_col_subject': 'Subject',
  'admin_notifications_col_timestamp': 'Timestamp',
  'admin_notifications_col_status': 'Status',
  'admin_notifications_filter_title': 'Filter Telemetry',
  'admin_notifications_status_filter': 'Read Status',
  'admin_notifications_type_filter': 'Event Category',
  'admin_notifications_clear_filters': 'Clear Filters',
  'admin_notifications_all_statuses': 'All Statuses',
  'admin_notifications_all_categories': 'All Categories',
  'admin_notifications_unread_only': 'Unread Only',
  'admin_notifications_read_only': 'Read Only',
  'admin_notifications_quick_categories': 'Quick Diagnostic Filters',
  'admin_notifications_modal_category': 'Category',
  'admin_notifications_modal_received': 'Received',
  'admin_notifications_action_link': 'View Details',
  'admin_notifications_close_btn': 'Close',
  'admin_notifications_status_unread': 'Unread',
  'admin_notifications_status_read': 'Read',

  // Circuit Breaker Labels
  'cb_mgmt_title': 'Resilience Management',
  'cb_mgmt_breaker_status': 'Breaker Status',
  'cb_mgmt_breaker_toggle': 'Toggle Breaker',
  'cb_mgmt_breaker_state_open': 'OPEN',
  'cb_mgmt_breaker_state_closed': 'CLOSED',
  'cb_mgmt_breaker_state_half_open': 'HALF-OPEN',
  'cb_mgmt_breaker_reset': 'Force Reset',
  'cb_mgmt_toggle_success': 'Circuit breaker status updated.',
  'cb_mgmt_toggle_error': 'Failed to update circuit breaker.',
  'cb_mgmt_healthy_services': 'Healthy Services',
  'cb_mgmt_active_incidents': 'Active Incidents (OPEN)',
  'cb_mgmt_action_required': 'Action Required',
  'cb_mgmt_optimal': 'Optimal',
  'cb_mgmt_description': 'Manual override console for institutional service resilience.',
  'cb_mgmt_refresh_diagnostics': 'Refresh Diagnostics',
  'cb_mgmt_col_engine': 'Service Engine',
  'cb_mgmt_col_state': 'Current State',
  'cb_mgmt_col_metrics': 'Metrics',
  'cb_mgmt_col_actions': 'Emergency Actions',
  'cb_mgmt_target_engine': 'Target Service Engine: ',
  'cb_mgmt_empty_state': 'No active circuit breakers discovered in the core engine.',
  'cb_mgmt_success_label': 'Success: ',
  'cb_mgmt_failures_label': 'Failures: ',
  'cb_mgmt_warning_title': 'Administrative Override Warning',
  'cb_mgmt_warning_desc': 'Manual circuit breaker overrides are forensic operations that bypass automated resilience logic. Forcefully resetting a breaker while a downstream service is still failing may lead to cascading system instability.',
  'cb_mgmt_initializing': 'Initializing Resilience Diagnostics...',
  'cb_mgmt_confirm_reset_title': 'Confirm Force Reset',
  'cb_mgmt_confirm_reset_desc': 'Are you sure you want to force reset this circuit breaker? Bypassing automated logic may cause system instability.',
  'cb_mgmt_confirm_open_title': 'Confirm Force Open',
  'cb_mgmt_confirm_open_desc': 'Are you sure you want to force open this circuit breaker? This will immediately fail all downstream requests for this service.',
  'cb_mgmt_info_tooltip': 'Autonomous resilience orchestration console. This interface allows administrators to monitor and manually intervene in the Circuit Breaker lifecycle. It provides forensic visibility into service health, failure trajectories, and enables emergency recovery operations to prevent cascading system failures during critical infrastructure degradation.',

  // User Management Page labels
  'user_mgmt_page_title': 'User & Access Management',
  'users_by_role_title': 'Users by Role',
  'filter_users_title': 'Filter Users',
  'search_users_label': 'Search by ID, Name, Email',
  'search_users_placeholder': 'Start typing to search...',
  'role_filter_label': 'Role',
  'role_filter_placeholder': 'All Roles',
  'status_filter_label': 'Status',
  'status_filter_placeholder': 'All Statuses',
  'role_users_status_filter_label': 'Filter by Status',
  'role_users_search_label': 'Search by ID, Name, Email',
  'role_users_filter_apply': 'Apply',
  'role_users_filter_reset': 'Reset',

  // User Summary Stats labels
  'system_user_analytics_title': 'System User Analytics',
  'live_updates_label': 'Live Updates',
  'create_single_user_button': 'Create Single User',
  'create_bulk_users_button': 'Create Bulk Users',
  'total_users_card_title': 'Total Users',
  'active_users_card_title': 'Active Users',
  'new_users_7days_card_title': 'New (7 days)',
  'locked_accounts_card_title': 'Locked Accounts',
  'pending_first_login_card_title': 'Pending 1st Login',

  // Analytics Dashboard labels
  'analytics_dashboard_title': 'Institutional Analytics',
  'revenue_trends_title': 'Revenue Trends',
  'payment_distribution_title': 'Payment Method Distribution',
  'enrollment_growth_title': 'Enrollment Growth',

  // Dashboard & Analytics Labels
  'total_payments_title': 'Total Payments',
  'total_students_title': 'Total Students',
  'total_scholarship_title': 'Total Scholarship',
  'daily_payment_trends_title': 'Daily Payment Trends',
  'new_student_registrations_title': 'New Student Registrations',
  'payment_methods_title': 'Payment Methods',
  'students_by_dept_title': 'Students by Dept',
  'payment_statuses_title': 'Payment Statuses',
  'scholarship_status_title': 'Scholarship Status',

  // Chart Labels
  'daily_payments_chart_label': 'Daily Payments (ETB)',
  'new_registrations_chart_label': 'New Registrations',

  // Status/Feedback Labels
  'loading_analytics_dashboard': 'Loading Analytics Dashboard...',
  'no_analytics_data_available': 'No analytics data available.',
  'error_title': 'Error',

  // Sidebar Labels
  'sidebar_university_logo_alt': 'University Logo',
  'sidebar_toggle_aria': 'Toggle Sidebar',
  'sidebar_no_links': 'No links available',
  'sidebar_roles_super_admin': 'Super Admin',
  'sidebar_common_user': 'User',
  'sidebar_auth_logout': 'Logout',
  'nav_academic_progression': 'Academic Progression',
  'Academic Progression': 'Academic Progression',
  'Academic Sessions': 'Academic Sessions',
  'Academic Record Entry': 'Academic Record Entry',
  'Eligibility Configuration': 'Eligibility Configuration',
  'nav_academic_sessions': 'Academic Sessions',
  'nav_academic_record_entry': 'Academic Record Entry',
  'nav_eligibility_config': 'Eligibility Configuration',
  'Promotion Previews': 'Promotion Previews',
  'Promotion Results': 'Promotion Results',

  // Academic Eligibility Configuration
  'academic_eligibility_desc': 'Configure the eligibility gates and timeouts that determine student promotion readiness.',
  'academic_eligibility_save_btn': 'Save Configuration',
  'academic_config_tab_global': 'Global Defaults',
  'academic_config_tab_departments': 'Department Overrides',
  'academic_config_tab_timeouts': 'Gate Timeouts',
  'academic_override_add_title': 'Add Override',
  'academic_select_dept': '-- Select Department --',
  'academic_add_override_btn': 'Add Override',
  'academic_overridden_depts': 'Overridden Departments',
  'academic_no_overrides': 'No department overrides configured.',
  'academic_editing_overrides_for': 'Overrides for:',
  'academic_clear_all_overrides': 'Delete All Overrides',
  'academic_overridden': 'Overridden',
  'academic_inherited': 'Inherited',
  'academic_override_check': 'Override',
  'academic_no_dept_selected': 'No Department Selected',
  'academic_select_dept_prompt': 'Select an overridden department from the list or add a new override configuration.',
  'academic_timeouts_title': 'Database & API Gate Timeouts',
  'academic_timeouts_desc': 'Set the execution timeout limits in milliseconds (ms) for each query module inside the promotion engine to prevent blocking the event loop.',
  'academic_timeout_lookup': 'Lookup Service (ms)',
  'academic_timeout_financial': 'Financial Queries (ms)',
  'academic_timeout_historical': 'Historical Analysis (ms)',
  'academic_timeout_default': 'Default Threshold (ms)',
  'academic_eligibility_confirm_msg': 'This will save the current Global Defaults, Department Overrides, and Gate Timeouts as a new configuration version. The new rules will be applied to all future promotion sessions. Are you sure you want to proceed?',

  // Gates Labels & Descriptions
  'academic_gate_1_label': 'Gate 1 — Fee Payment',
  'academic_gate_1_desc': 'Student must meet the minimum payment percentage and maximum outstanding balance thresholds.',
  'academic_gate_1_min_payment_pct_label': 'Minimum Payment Percentage (%)',
  'academic_gate_1_max_outstanding_bal_label': 'Maximum Outstanding Balance',
  'academic_gate_1_carry_forward_label': 'Carry Forward Unpaid Balance',
  'academic_gate_2_label': 'Gate 2 — Payment Verification',
  'academic_gate_2_desc': 'Student must not have unverified payments in progress.',
  'academic_gate_3_label': 'Gate 3 — Penalty Clearance',
  'academic_gate_3_desc': 'Student must clear all outstanding financial penalties.',
  'academic_gate_4_label': 'Gate 4 — Installment Compliance',
  'academic_gate_4_desc': 'Student must be compliant with their installment plans.',
  'academic_gate_4_grace_period_label': 'Installment Grace Period (Days)',
  'academic_gate_5_label': 'Gate 5 — Minimum GPA',
  'academic_gate_5_desc': 'Student must meet the minimum required GPA threshold.',
  'academic_gate_5_gpa_threshold_label': 'Minimum GPA Threshold',
  'academic_gate_6_label': 'Gate 6 — Course Completion',
  'academic_gate_6_desc': 'Student must have earned the minimum required credit hours.',
  'academic_gate_6_min_credits_label': 'Minimum Credits Per Semester',
  'academic_gate_7_label': 'Gate 7 — Registration Status',
  'academic_gate_7_desc': 'Student must have an active registration status.',
  'academic_gate_8_label': 'Gate 8 — Disciplinary Clearance',
  'academic_gate_8_desc': 'Student must not have active disciplinary holds or suspensions.',
  'academic_gate_9_label': 'Gate 9 — Scholarship Reconciliation',
  'academic_gate_9_desc': 'Student scholarship decisions must be reconciled or final.',
  'academic_gate_9_deadline_label': 'Scholarship Decision Deadline (Days)',
  'academic_gate_10_label': 'Gate 10 — Previous Debt Check',
  'academic_gate_10_desc': 'Student carried debt from previous terms must not exceed threshold.',
  'academic_gate_10_max_semesters_label': 'Maximum Carryover Semesters',
  'academic_gate_11_label': 'Gate 11 — Fee Record Required',
  'academic_gate_11_desc': 'Student must have a fee record registered for the academic term.',

  // Admin Dashboard Labels
  'admin_dashboard_page_title': 'Administrator Dashboard',
  'admin_dashboard_loading': 'Loading Dashboard Statistics...',
  'admin_dashboard_error_title': 'Dashboard Error',
  'admin_dashboard_api_error': 'Unable to load dashboard statistics. The backend API may be unavailable.',
  'admin_dashboard_user_dist_title': 'User Distribution',
  'admin_dashboard_scholarship_apps_title': 'Scholarship Applications Overview',
  'admin_dashboard_scholarship_programs_title': 'Scholarship Programs',
  'admin_dashboard_staff_accounts_title': 'Staff Accounts',

  // System Configuration Labels
  'sys_config_page_title': 'System Configuration',
  'sys_config_maintenance_warning': 'SYSTEM IS CURRENTLY IN MAINTENANCE MODE!',
  'sys_config_tab_general': 'General',
  'sys_config_tab_departments': 'Departments',
  'sys_config_tab_year_levels': 'Year Levels',
  'sys_config_tab_semesters': 'Semesters',
  'sys_config_tab_roles': 'Roles & Permissions',
  'sys_config_tab_security': 'Security',
  'sys_config_tab_financial': 'Financial Policies',
  'sys_config_tab_notifications': 'Notifications',
  'sys_config_tab_maintenance': 'Maintenance',
  'sys_config_tab_payment': 'Payment Gateways',
  'sys_config_tab_feature_flags': 'Feature Flags',
  'sys_config_tab_lookups': 'Lookups',
  'sys_config_tab_navigation': 'Navigation Settings',
  'sys_config_tab_frontend': 'Frontend & Theme',
  'sys_config_tab_resilience': 'Resilience Management',
  'sys_config_roles_moved': 'Role and permission management has been moved to its own dedicated page.',
  'sys_config_go_to_roles': 'Go to Role Management',
  'sys_config_navigation_moved': 'UI navigation and menu management has been moved to its own dedicated page.',
  'sys_config_go_to_navigation': 'Go to Navigation Settings',
  'sys_config_under_construction': 'This section is under construction.',
  'sys_config_settings_suffix': 'Settings',
  'sys_config_tab_payment_gateway_mgmt': 'Payment Gateway Management',

  // Departments Management
  'admin_departments_col_name': 'Department Name',
  'admin_departments_col_semesters': 'Semesters/Year',
  'admin_departments_col_max_years': 'Max Year Levels',
  'admin_departments_col_students': 'Student Count',
  'admin_departments_col_fee_structures': 'Fee Structures',
  'admin_departments_col_actions': 'Actions',
  'admin_departments_add_new_title': 'Add New Department',
  'admin_departments_add_new_placeholder': 'e.g., Civil Engineering',
  'admin_departments_create_btn': 'Create',
  'admin_departments_empty_message': 'No departments found.',
  'admin_departments_edit_modal_title': 'Edit Department',
  'admin_departments_edit_modal_desc': 'Editing "{name}" will update it everywhere it is used.',
  'admin_departments_edit_modal_save_btn': 'Save Changes',
  'admin_departments_delete_modal_title': 'Confirm Deletion',
  'admin_departments_delete_modal_desc': 'Are you sure you want to permanently delete the department "{name}"? This action cannot be undone.',
  'admin_departments_delete_modal_btn': 'Delete',

  // Notification Template Placeholder Descriptions
  'placeholder_desc_fullName': 'Full name of the notification recipient',
  'placeholder_desc_universityName': 'Official name of the university',
  'placeholder_desc_systemName': 'Name of the system platform',
  'placeholder_desc_username': 'Login username of the recipient',
  'placeholder_desc_temporaryPassword': 'Auto-generated temporary password',
  'placeholder_desc_loginUrl': 'Direct URL to the login page',
  'placeholder_desc_resetLink': 'Secure password reset link',
  'placeholder_desc_currency': 'Configured currency code (e.g., ETB)',
  'placeholder_desc_amount': 'Transaction amount value',
  'placeholder_desc_paymentMethod': 'Payment method used',
  'placeholder_desc_transactionId': 'Unique gateway transaction identifier',
  'placeholder_desc_formatDate_paymentDate': 'Formatted date of the payment',
  'placeholder_desc_reason': 'Reason message for the action',
  'placeholder_desc_feeStructure_name': 'Name of the fee structure',
  'placeholder_desc_feeStructure_totalFee': 'Total amount due for the fee structure',
  'placeholder_desc_feeStructure_academicYear': 'Academic year of the fee structure',
  'placeholder_desc_feeStructure_semesterName': 'Semester name for the fee period',
  'placeholder_desc_feeStructure_departmentName': 'Department associated with the fee',
  'placeholder_desc_feeStructure_yearLevelName': 'Year level for the fee structure',
  'placeholder_desc_formatDate_feeStructure_fixedDueDate': 'Formatted payment deadline date',
  'placeholder_desc_formatDate_feeStructure_effectiveDate': 'Formatted effective start date',
  'placeholder_desc_feeStructure_notes': 'Administrative notes on the fee structure',
  'placeholder_desc_studentName': 'Full name of the student',
  'placeholder_desc_universityId': 'Official university ID of the student',
  'placeholder_desc_creatorName': 'Name of the secure note creator',
  'placeholder_desc_secretId': 'Unique identifier of the secure note',
  'placeholder_desc_totalAmountReconciled': 'Total amount successfully reconciled',
  'placeholder_desc_paymentId': 'Internal payment record identifier',
  'placeholder_desc_reconciledFeesSummary': 'Summary of reconciled fee items',
  'placeholder_desc_failureCount': 'Number of detected failures',
  'placeholder_desc_windowMinutes': 'Time window in minutes for anomaly detection',
  'placeholder_desc_dashboardLink': 'Direct link to the observability dashboard',
  'placeholder_desc_balance': 'Outstanding balance amount',
  'placeholder_desc_daysRemaining': 'Days remaining until the deadline',
  'placeholder_desc_deadline': 'Full deadline date string',
  'placeholder_desc_dueDate': 'Due date for the installment',
  'placeholder_desc_installmentNumber': 'Sequential number of the installment',
  'placeholder_desc_supportEmail': 'Institutional IT Support Email Address',
  'placeholder_desc_supportPhone': 'Institutional IT Support Phone Number',
  'placeholder_desc_scholarshipName': 'Name of the scholarship program',
  'placeholder_desc_benefitType': 'Type of scholarship benefit (e.g., Fixed, Percentage)',
  'placeholder_desc_benefitValue': 'Value or amount of the scholarship benefit',
  'placeholder_desc_previousBalance': 'Account balance before the change',
  'placeholder_desc_adjustedBalance': 'Account balance after the change',
  'placeholder_desc_lockReason': 'Reason for the account lock',
  'placeholder_desc_lockDuration': 'Duration of the account lock',
  'placeholder_desc_overdueAmount': 'Total amount currently overdue',
  'placeholder_desc_daysOverdue': 'Number of days the payment is overdue',
  'placeholder_desc_nextEscalationDate': 'Date of the next dunning escalation',
  'placeholder_desc_consequenceDescription': 'Description of the consequence for non-payment',
  'placeholder_desc_creditAmount': 'Amount of credit available',
  'placeholder_desc_totalPaid': 'Total amount paid',
  'placeholder_desc_totalFeesDue': 'Total fees due',
  'placeholder_desc_refundPolicyNote': 'Note regarding the refund policy',
  'placeholder_desc_changeReason': 'Reason for the balance or state change',
  'placeholder_desc_effectiveDate': 'Date when the change becomes effective',
  'placeholder_desc_installmentAmount': 'Amount required for the specific installment',
  'placeholder_desc_installmentDueDate': 'Due date for the specific installment',
  'placeholder_desc_totalRemainingBalance': 'Total remaining active balance to be paid',
  'placeholder_desc_planName': 'Name of the installment or payment plan',

  //payment gateway tab
  'payment_gateway_delete_title': 'Delete Payment Gateway',
  'payment_gateway_delete_message_detailed': 'Are you sure you want to delete the gateway "{name}"? This action is permanent and may impact active payment links or integration stability.',
  'payment_gateway_delete_success': 'Gateway "{name}" deleted successfully.',

  // Observability Dashboard Labels
  'observability_chart_latency_label': 'Latency (Count)',
  'observability_chart_request_count': 'Request Count',
  'observability_chart_api_latency': 'API Latency',
  'observability_chart_error_count': 'Error Count',
  'observability_chart_total_fallback': 'Total',
  'observability_chart_unknown_fallback': 'unknown',
  'observability_status_delivered': 'Delivered',
  'observability_status_pending': 'Pending',
  'observability_status_failed': 'Failed',
  'observability_section_health': 'System Health Overview',
  'observability_page_desc': 'High-fidelity instrumentation of core engine performance and delivery telemetry.',
  'common_refresh': 'Refresh Data',

  // Maintenance Labels
  'maintenance_title': 'Under Maintenance',
  'maintenance_default_message': 'The system is currently unavailable. We are performing scheduled maintenance and will be back online shortly. Thank you for your patience.',

  // Workflow Designer Labels
  'workflow_designer_page_title': 'Workflow Designer',
  'workflow_designer_subtitle': 'Design and manage automated workflows.',
  'workflow_designer_create_btn': 'Create Workflow',
  'workflow_designer_save_btn': 'Save Workflow',
  'workflow_designer_loading': 'Loading designer...',
  'workflow_designer_fetch_error': 'Failed to load workflow designer.',

  // Authentication & Landing Labels
  'landing_welcome_title': 'Welcome to Dambi Dollo University',
  'landing_subtitle': 'Select your portal to continue.',
  'landing_login_student': 'Student Portal',
  'landing_login_staff': 'Staff Portal',
  'landing_login_admin': 'Admin Portal',
  'auth_login_title': 'Sign In',
  'auth_login_subtitle': 'Access your account',
  'auth_username_placeholder': 'Enter your username',
  'auth_password_placeholder': 'Enter your password',
  'auth_login_btn': 'Sign In',
  'auth_forgot_password': 'Forgot Password?',
  'auth_back_to_home': 'Back to Home',
  'auth_login_error_invalid_credentials': 'Invalid username or password. Please try again.',
  'auth_login_error_account_locked': 'This account has been temporarily locked due to multiple failed login attempts. Please try again later.',
  'auth_login_error_empty_username': 'Username is required.',
  'auth_login_error_empty_password': 'Password is required.',

  // Reauthentication Modal Labels
  'auth_reauth_modal_title': 'Confirm Your Identity',
  'auth_reauth_modal_description': 'For security purposes, please enter your password to continue with this action.',
  'auth_reauth_modal_confirm_button': 'Confirm & Continue',
  'auth_reauth_modal_cancel_button': 'Cancel',
  'auth_reauth_modal_password_label': 'Password',
  'auth_reauth_modal_password_placeholder': 'Enter your current password',
  'auth_reauth_modal_verification_badge': 'Secure Identity Verification',
  'auth_reauth_modal_verifying_text': 'Verifying...',
  'auth_reauth_modal_error_invalid': 'Incorrect password. Please try again.',
  'auth_reauth_modal_error_rate_limit': 'Too many attempts. Please wait a moment and try again.',
  'auth_reauth_modal_error_network': 'Network error. Please check your connection.',
  'auth_reauth_modal_error_unknown': 'An error occurred. Please try again.',
  'auth_reauth_modal_error_required': 'Password is required.',


  // Admin Profile Settings Labels
  'profile_page_title': 'My Profile & Settings',
  'profile_hero_title_account': 'Account',
  'profile_hero_title_settings': 'Settings',
  'profile_hero_subtitle': 'Manage your personal information, security preferences, and system notifications.',
  'profile_tab_info': 'Profile Info',
  'profile_tab_password': 'Security',
  'profile_tab_notifications': 'Notifications',
  'profile_tab_activity': 'Activity Log',
  'profile_tab_2fa': '2FA',
  'profile_fetch_error': 'Failed to fetch latest profile data.',

  // Admin Profile - Profile Info Tab Labels
  'profile_info_section_identity': 'System Identity',
  'profile_info_label_user_id': 'User ID',
  'profile_info_label_username': 'Username',
  'profile_info_label_role': 'Primary Role',
  'profile_info_label_full_name': 'Full Name',
  'profile_info_placeholder_full_name': 'Enter your full name',
  'profile_info_label_email': 'Email Address',
  'profile_info_placeholder_email': 'your.email@example.com',
  'profile_info_button_save': 'Save Changes',
  'profile_info_button_saving': 'Saving...',

  // Admin Profile - Security/Password Tab Labels
  'password_hero_title': 'Secure Your Account',
  'password_hero_tooltip': 'Update your password periodically to maintain high security.',
  'password_label_current': 'Current Password',
  'password_label_new': 'New Password',
  'password_label_confirm': 'Confirm New Password',
  'password_requirements_title': 'Requirements',
  'password_requirement_min_length': '{length}+ characters',
  'password_requirement_uppercase': 'Uppercase letter',
  'password_requirement_lowercase': 'Lowercase letter',
  'password_requirement_number': 'Number',
  'password_requirement_symbol': 'Symbol',
  'password_button_update': 'Update Password',

  // Admin Profile - Notifications Tab Labels
  'notification_hero_title': 'Notification Center',
  'notification_hero_subtitle': 'Manage how you receive alerts and system updates.',
  'notification_feedback_title': 'Student Feedback',
  'notification_feedback_tooltip': 'Receive an email whenever a student submits a new feedback or complaint.',
  'notification_critical_title': 'System Critical Alerts',
  'notification_critical_tooltip': 'Receive immediate email alerts for 500 errors, security breaches, or downtimes.',
  'notification_recommended_badge': 'Highly Recommended',
  'notification_button_save': 'Save Preferences',

  // Admin Profile - Activity Log Tab Labels
  'activity_log_hero_title': 'Security Log',
  'activity_log_hero_tooltip': 'Recent actions and sign-in attempts for your account.',
  'activity_log_table_col_timestamp': 'Timestamp',
  'activity_log_table_col_action': 'Action',
  'activity_log_table_col_target': 'Target',
  'activity_log_table_col_details': 'Details',
  'activity_log_pagination_info': 'Page {page} of {totalPages}',

  // Admin Profile - 2FA Tab Labels
  'profile_2fa_hero_title': 'Two-Factor Authentication',
  'profile_2fa_hero_tooltip': 'Protect your account by requiring an additional code when logging in. We support Authy, Google Authenticator, and 1Password.',
  'profile_2fa_status_label': 'Status',
  'profile_2fa_status_enabled': 'Enabled & Secure',
  'profile_2fa_status_disabled': 'Disabled',
  'profile_2fa_step1_label': 'Step 1: Scan Code',
  'profile_2fa_step2_label': 'Step 2: Verify Code',
  'profile_2fa_start_setup': 'Enable Two-Factor Authentication',
  'profile_2fa_verify': 'Verify',
  'profile_2fa_disable': 'Disable 2FA',
  'profile_2fa_disable_confirm_title': 'Disable Two-Factor Authentication',
  'profile_2fa_disable_confirm_msg': 'Are you sure you want to disable 2FA? This will significantly reduce your account security.',

  // Admin Profile - Error Messages
  'profile_update_success': 'Profile updated successfully.',
  'profile_update_error': 'Failed to update profile.',
  'password_update_success': 'Password updated successfully. Please log in again.',
  'password_update_error': 'Failed to update password.',
  'password_strength_error': 'New password does not meet all security requirements.',
  'notification_fetch_error': 'Failed to fetch notification preferences.',
  'notification_update_success': 'Notification preferences updated successfully.',
  'notification_update_error': 'Failed to update preferences.',
  'profile_2fa_setup_error': 'Failed to start 2FA setup.',
  'profile_2fa_verify_success': 'Two-factor authentication enabled successfully.',
  'profile_2fa_verify_error': 'Failed to verify code.',
  'profile_2fa_disable_success': '2FA has been disabled.',
  'profile_2fa_disable_error': 'Failed to disable 2FA.',

  // Bulk User Creation Labels
  'bulk_user_page_title': 'Bulk Create Users',
  'bulk_user_wizard_title': 'Bulk Student Creation',
  'bulk_user_step1_title': 'Step 1: Upload CSV File',
  'bulk_user_step1_desc': 'Upload a CSV file with student data. A unique, temporary password will be generated for each user and sent via email.',
  'bulk_user_step2_title': 'Step 2: Validate & Confirm',
  'bulk_user_step3_title': 'Step 3: Processing Complete',
  'bulk_user_upload_placeholder': 'Drag & drop your CSV file here, or click to select',
  'bulk_user_download_template': 'Download template',
  'bulk_user_validation_success': 'Validation Successful. Found {count} student records to create.',
  'bulk_user_validation_failed_title': 'File Validation Failed',
  'bulk_user_invalid_file_error': 'Invalid file type. Please upload a CSV file.',
  'bulk_user_processing_title': 'Processing...',
  'bulk_user_processing_desc': 'Creating student accounts. Please do not navigate away from this page.',
  'bulk_user_result_successful': 'Successful',
  'bulk_user_result_failures': 'Failures',
  'bulk_user_result_success_tab': 'Success ({count})',
  'bulk_user_result_failures_tab': 'Failures ({count})',
  'bulk_user_search_placeholder': 'Search results...',
  'bulk_user_download_failures': 'Download Failures as CSV',
  'bulk_user_header_username': 'Username',
  'bulk_user_header_fullname': 'Full Name',
  'bulk_user_header_email': 'Email',
  'bulk_user_header_status': 'Status',
  'bulk_user_header_id': 'University ID',
  'bulk_user_header_error': 'Error',
  'bulk_user_header_actions': 'Actions',
  'bulk_user_retried_status': '✓ Retried',
  'bulk_user_edit_retry_button': 'Edit & Retry',
  'bulk_user_retry_button': 'Retry',
  'bulk_user_cancel_button': 'Cancel',
  'bulk_user_start_new_button': 'Start New Bulk Upload',

  // Audit Log Viewer Labels
  'audit_log_page_title': 'Audit Log Viewer',
  'audit_log_filter_title': 'Filter Logs',
  'audit_log_loading': 'Loading audit logs...',
  'audit_log_fetch_error': 'Failed to fetch audit logs.',
  'audit_log_actor_placeholder': 'Actor User ID...',
  'audit_log_entity_placeholder': 'Entity Type...',
  'audit_log_entity_id_placeholder': 'Entity ID...',

  // Create User Page Labels
  'create_user_title': 'Create New User',
  'update_user_title': 'Update User',
  'create_user_basic_info': 'Basic Information',
  'create_user_roles': 'Assign Roles',
  'create_user_student_details': 'Student Details',
  'create_user_account_settings': 'Account Settings',
  'create_user_loading': 'Loading user data...',
  'create_user_system_managed_username': 'System-Managed Username',
  'create_user_username_label': 'Username',
  'create_user_username_placeholder': 'e.g., chala_tola',
  'create_user_full_name_label': 'Full Name',
  'create_user_full_name_placeholder': 'e.g., Chala Tola Bulcha',
  'create_user_cultural_override_label': 'Single/Non-Standard Name Override (Remove Parts Requirement)',
  'create_user_email_label': 'Email Address',
  'create_user_email_placeholder': 'e.g., user@university.edu.et',
  'create_user_phone_label': 'Phone Number',
  'create_user_assign_roles_header': 'Assign System Roles',
  'create_user_student_details_header': 'Academic/Student Details',
  'create_user_university_id_label': 'University ID',
  'create_user_university_id_placeholder': 'Enter official ID number',
  'create_user_department_label': 'Department',
  'create_user_select_department_placeholder': 'Select Department',
  'create_user_year_level_label': 'Year Level',
  'create_user_select_year_level_placeholder': 'Select Year Level',
  'create_user_semester_label': 'Current Semester',
  'create_user_select_semester_placeholder': 'Select Semester',
  'create_user_account_settings_header': 'Account & Security Settings',
  'create_user_account_status_label': 'Account Status',
  'create_user_select_status_placeholder': 'Select Initial Status',
  'create_user_initial_password_title': 'Initial Credentials',
  'create_user_initial_password_desc': 'A temporary password will be automatically generated and provided to the user.',
  'create_user_delivery_method_header': 'Credential Delivery Method',
  'create_user_standard_email_option': 'Standard Email (Plaintext temporary password)',
  'create_user_sms_delivery_option': 'Direct SMS',
  'create_user_requires_phone_suffix': '(Requires Phone Number)',
  'create_user_secure_note_option': 'Secure One-Time Note (Self-destructing link)',
  'create_user_secure_note_desc': 'Highest security. You will receive a link to share with the user.',
  'create_user_delivery_channels_header': 'SECURE NOTE DELIVERY CHANNELS',
  'create_user_send_via_email_label': 'Send link via Email',
  'create_user_send_via_sms_label': 'Send link via SMS',
  'create_user_creating_status': 'Creating Account...',
  'create_user_updating_status': 'Updating Account...',
  'create_user_create_button': 'Create User Account',
  'create_user_save_button': 'Save User Changes',
  'create_user_cancel_button': 'Cancel',
  'create_user_done_button': 'Done',
  'create_user_note_generated_title': 'Secure Access Link Generated',
  'create_user_note_generated_desc': 'This link contains the temporary credentials and will expire after one viewing.',
  'create_user_note_burn_warning': 'IMPORTANT: This link can only be opened ONCE. Copy it now and share it securely.',
  'create_user_note_copy_button': 'Copy Secret Link',
  'create_user_username_disclaimer': '* A unique discriminator will be added if this username is taken.',
  'create_user_name_validation_standard': 'Full name (First Middle Last) is required.',
  'create_user_name_validation_middle': 'Middle name or initial is required.',
  'create_user_name_validation_last': 'Last name (Family name) is required.',
  'create_user_required_fields_error': 'Please fill in all required fields marked with *.',
  'create_user_updated_success': 'User {name} updated successfully.',
  'create_user_created_success': 'User {name} created successfully.',
  'create_user_email_sent_suffix': 'Email',
  'create_user_sent_via': 'Sent via {channels}.',
  'create_user_name_parts': 'Parts',
  'common_error': 'Error',
  'common_currency_etb': 'ETB',
  'common_not_applicable': 'N/A',
  'common_validation_failed': 'Validation failed.',
  'common_total': 'Total',
  'common_close': 'Close',
  'common_cancel': 'Cancel',
  'common_view': 'View',
  'common_log': 'Log',
  'common_details': 'Details',
  'common_scheduling': 'Scheduling',
  'common_none': 'None',
  'common_previous': 'Previous',
  'common_next': 'Next',
  'common_back': 'Return',
  'fees_table_academic_term_label': 'Semester',
  'fees_table_year_level_prefix': 'Year:',
  'fees_table_scheduled_prefix': 'Scheduled:',
  'fees_table_due_date_prefix': 'Due Date:',
  'fees_table_expires_prefix': 'Expires:',

  // Common Category Labels
  'common_category_payment': 'Payment',
  'common_category_deadline': 'Deadline',
  'common_category_announcement': 'Announcement',
  'common_category_scholarship': 'Scholarship',
  'common_category_security': 'Security',
  'common_category_registration': 'Registration',
  'common_category_discrepancy': 'Discrepancy',
  'common_category_system_alert': 'System Alert',
  'common_category_approval': 'Approval',
  'common_category_success': 'Success',

  // Error & Feedback Labels
  'errors_unexpected': 'An unexpected error occurred. Please try again later.',
  'errors_failed_to_get_overview': 'Failed to retrieve requested data.',
  'secure_note_copied_toast': 'Link copied to clipboard!',

  // Role Management Labels
  'role_mgmt_institutional_context': 'Role management system accessed within institutional compliance framework.',
  'role_mgmt_page_title': 'Roles & Permissions',
  'role_mgmt_create_button': '+ Create New Role',
  'role_mgmt_table_col_name': 'Role Name',
  'role_mgmt_table_col_desc': 'Description',
  'role_mgmt_table_col_users': 'Users',
  'role_mgmt_table_col_actions': 'Actions',
  'role_mgmt_view_users_tooltip': 'View Assigned Users',
  'role_mgmt_edit_perms': 'Edit Permissions',
  'role_mgmt_delete': 'Delete',
  'role_mgmt_delete_confirm_title': 'Authorize Deletion',
  'role_mgmt_delete_confirm': 'Are you sure you want to delete the "{name}" role? This cannot be undone.',
  'role_mgmt_delete_confirm_btn': 'Execute Deletion',
  'role_mgmt_system_role_protected': 'Core system roles cannot be deleted.',
  'role_mgmt_has_users_warning': 'Unassign all users before deletion.',
  'role_mgmt_create_modal_title': 'Create New Role',
  'role_mgmt_name_label': 'Role Name*',
  'role_mgmt_desc_label': 'Description',
  'role_mgmt_subtitle': 'Define user roles and assign specific permissions to control access.',
  'role_mgmt_empty': 'No roles defined in the current security matrix.',
  'role_mgmt_create_error': 'Failed to create role.',
  'role_mgmt_delete_error': 'Failed to delete role.',
  'role_mgmt_cancel': 'Cancel',
  'role_mgmt_create_btn': 'Create',
  'role_mgmt_processing': 'Processing...',
  'role_mgmt_created_success': 'Role created successfully.',
  'role_mgmt_delete_success': 'Role deleted.',
  'role_mgmt_fetch_error': 'Failed to fetch roles.',

  // Role Users Page Labels
  'role_users_page_title': '{role} Users',
  'role_users_subtitle': 'Manage all users with the {role} role',
  'role_users_back_aria': 'Back to User Management',
  'role_users_create_btn': 'Create User',
  'role_users_total_stats': 'Total Users',
  'role_users_loading': 'Loading {role} users...',
  'role_users_status_modal_title': 'Change Status for {name}',
  'role_users_status_select_label': 'Select the new account status for {username}.',
  'role_users_update_status_btn': 'Update Status',
  'role_users_reset_pass_modal_title': 'Reset Password for {name}',
  'role_users_reset_pass_confirm': 'Are you sure you want to reset the password for {username}?',
  'role_users_reset_pass_confirm_btn': 'Reset Vectors',
  'role_users_delete_modal_title': 'Delete User {name}',
  'role_users_delete_confirm': 'Are you sure you want to permanently delete the user {username}? This action cannot be undone.',
  'role_users_status_changed_success': 'Status for {name} changed to {status}.',
  'role_users_pass_reset_success': 'Password for {name} has been reset.',
  'role_users_delete_success': 'User {name} deleted successfully.',
  'role_users_not_found': 'Role "{role}" not found.',
  'role_users_pagination_info': 'Page {page} of {totalPages} (Total: {total} users)',
  'role_users_selection_active': 'Selection Active',
  'role_users_selection_desc': '{count} item(s) selected for bulk management.',
  'bulk_action_status': 'Change Status',
  'bulk_action_reset': 'Reset Passwords',
  'bulk_action_delete': 'Delete Selected',
  'role_users_bulk_action_success': 'Bulk {action} completed for {count} users.',

  // Role Details Page Labels
  'role_details_institutional_context': 'Role permissions accessed within institutional compliance framework.',
  'role_details_title': 'Manage Role: {name}',
  'role_details_header': 'Role: {name}',
  'role_details_perms_header': 'Permissions',
  'role_details_saving': 'Saving changes...',
  'role_details_perm_assigned_success': 'Permission assigned successfully.',
  'role_details_perm_unassigned_success': 'Permission unassigned successfully.',
  'role_details_fetch_error': 'Failed to fetch role details or permissions.',
  'role_details_not_found_message': 'Role details could not be loaded.',
  'role_details_update_error': 'Failed to update permission.',

  // Communication Rules Labels
  'comm_rules_page_title': 'Communication Rules',
  'comm_rules_page_description': 'Manage automated communication triggers, conditions, and templates for institutional engagement.',
  'comm_rules_create_btn': 'Create New Rule',
  'comm_rules_loading': 'Loading rules...',
  'comm_rules_fetch_error': 'Failed to fetch communication rules.',
  'comm_rules_delete_confirm': 'Are you sure you want to delete the rule "{name}"?',
  'comm_rules_delete_success': 'Rule deleted successfully.',
  'comm_rules_editor_title_new': 'Create Communication Rule',
  'comm_rules_editor_title_edit': 'Edit Communication Rule',
  'comm_rules_editor_name_label': 'Rule Name',
  'comm_rules_editor_name_tooltip': 'A unique, human-readable identifier for this communication rule. Used in audit logs and admin dashboards.',
  'comm_rules_editor_trigger_label': 'Trigger Event Name',
  'comm_rules_editor_trigger_tooltip': 'The exact domain event name that activates this rule (e.g., student.financials.recalculated). Must match an event published by the backend event bus.',
  'comm_rules_editor_trigger_placeholder': 'e.g., student.financials.recalculated',
  'comm_rules_editor_conditions_label': 'Conditions (JSON)',
  'comm_rules_editor_conditions_tooltip': 'A JSON object defining the criteria that must be met for this rule to fire. Leave as {} to match all events of the trigger type.',
  'comm_rules_editor_actions_label': 'Actions (JSON Array)',
  'comm_rules_editor_actions_tooltip': 'A JSON array of action objects to execute when conditions are met. Each action specifies a type (e.g., send_email) and its configuration.',
  'comm_rules_editor_priority_label': 'Priority',
  'comm_rules_editor_priority_tooltip': 'Numeric execution priority. Lower values execute first. Use 100 as the default. Rules with equal priority execute in creation order.',
  'comm_rules_editor_enabled_label': 'Rule Enabled',
  'comm_rules_save_btn': 'Save Rule',
  'comm_rules_created_success': 'Rule created successfully.',
  'comm_rules_updated_success': 'Rule updated successfully.',
  'comm_rules_conditions_json_error': 'Conditions field contains invalid JSON. Please correct the syntax and try again.',
  'comm_rules_actions_json_error': 'Actions field contains invalid JSON. Please correct the syntax and try again.',
  'comm_rules_delete_error': 'Failed to delete communication rule.',
  'comm_rules_restore_queued': 'Restoration of rule "{name}" queued.',
  'comm_rules_restore_success': 'Rule "{name}" restored.',
  'comm_rules_restore_error': 'Failed to restore rule.',
  'undo': 'Undo',
  'redo': 'Redo',

  // Audit Log Labels
  'audit_log_page_description': 'Forensic investigation console for tracking all system mutations, user actions, and compliance events with full traceability.',

  // Financial Insights Labels
  'fin_insights_page_title': 'Financial Insights',
  'fin_insights_subtitle': 'Real-time revenue intelligence and growth forecasting.',
  'fin_insights_sync_btn': 'Sync Data',
  'fin_insights_total_revenue': 'Total Revenue',
  'fin_insights_growth_trajectory': 'Growth Trajectory',
  'fin_insights_settlement_velocity': 'Settlement Velocity',
  'fin_insights_revenue_projection': 'Revenue Projection',
  'fin_insights_liquidity_forecast': 'Liquidity Forecast',
  'fin_insights_loading': 'Loading financial insights...',
  'fin_insights_fetch_error': 'Failed to fetch financial insights.',
  'fin_insights_period_label': 'Period',
  'fin_insights_start_date': 'Start Date',
  'fin_insights_end_date': 'End Date',
  'fin_insights_growth_period': 'period growth',
  'fin_insights_projection_desc': 'Next {months} months linear regression model',
  'fin_insights_liquidity_desc': '{days}-day daily net cash flow prediction',
  'user_profile_edit_title': 'Edit Your Profile',

  // Scholarship Management Labels
  'scholarship_mgmt_page_title': 'Scholarship Registry',
  'scholarship_mgmt_recent_title': 'Institutional Applications',
  'scholarship_mgmt_total_apps': 'Total Applications',
  'scholarship_mgmt_pending_finance': 'Under Finance Review',
  'scholarship_mgmt_pending_activation': 'Awaiting Activation',
  'scholarship_mgmt_active': 'Active Grants',
  'scholarship_mgmt_create_btn': '+ New Application',
  'scholarship_mgmt_view_all_btn': 'View All Records',
  'scholarship_mgmt_fetch_error': 'Failed to synchronize scholarship telemetry.',
  'scholarship_mgmt_table_empty': 'No scholarship records discovered.',
  'scholarship_mgmt_table_col_student': 'Applicant',
  'scholarship_mgmt_table_col_scholarship': 'Scholarship Name*',
  'scholarship_mgmt_table_col_status': 'Current State',
  'scholarship_mgmt_table_col_date': 'Submission Date',
  'scholarship_mgmt_table_col_actions': 'Operations',
  'scholarship_mgmt_view_manage': 'Inspect / Manage',

  // Scholarship Applications Page
  'scholarship_apps_page_title': 'Scholarship Applications',
  'scholarship_apps_filter_status': 'Filter by Lifecycle Status',
  'scholarship_apps_all_statuses': 'All Statuses',

  // Scholarship Application Details
  'scholarship_details_page_title': 'Manage Application - {name}',
  'scholarship_details_section_student': 'Applicant Information',
  'scholarship_details_section_program': 'Scholarship Details',
  'scholarship_details_section_status': 'Application Status',
  'scholarship_details_action_approve': 'Approve & Activate',
  'scholarship_details_action_reject': 'Decline Application',
  'scholarship_mgmt_table_col_type': 'Type',
  'scholarship_mgmt_table_col_benefit': 'Benefit',
  'scholarship_mgmt_table_col_assigned_to': 'Assigned To',
  'scholarship_mgmt_filter_type': 'Filter by Type',
  'scholarship_mgmt_all_types': 'All Types',
  'scholarship_mgmt_assigned_to_me': 'Assigned to Me',
  'scholarship_mgmt_unassigned': 'Unassigned',
  'scholarship_mgmt_search_placeholder': 'Search student name or ID...',
  'scholarship_search_student_placeholder': 'Search by name, ID, or email...',
  'scholarship_search_no_results': 'No students found matching your search.',
  'scholarship_details_section_review': 'Review & Governance',
  'scholarship_details_label_finance_status': 'Finance Review Status',
  'scholarship_details_label_finance_staff': 'Assigned Finance Staff',
  'scholarship_details_label_remarks': 'Reviewer Remarks',
  'scholarship_details_placeholder_remarks': 'Enter internal review notes...',
  'scholarship_details_doc_desc_placeholder': 'e.g., Letter of Recommendation, Medical Certificate, etc.',
  'scholarship_details_action_save_review': 'Save Review Progress',
  'scholarship_details_action_reassign': 'Reassign Staff',
  'scholarship_details_action_revert': 'Revert to Draft',
  'scholarship_details_toast_review_saved': 'Review progress saved successfully.',
  'scholarship_details_toast_reassigned': 'Application reassigned to {staffName}.',
  'scholarship_unassigned_staff': 'Unassigned (Any Finance Staff)',
  'scholarship_confirm_delete_title': 'Confirm Deletion',
  'scholarship_confirm_delete_msg': 'Are you sure you want to delete this scholarship application? This action cannot be undone if performed permanently.',
  'scholarship_confirm_activate_title': 'Confirm Scholarship Activation',
  'scholarship_confirm_activate_desc': 'Are you sure you want to activate this scholarship for {name}? This will apply the financial benefit to their account for the current term and cannot be undone.',
  'scholarship_documents_title': 'Application Documents',
  'scholarship_no_documents': 'No documents attached yet. 📂',
  'scholarship_add_document_btn': 'Add Document to Application',
  'scholarship_select_file_label': 'Select Document File',
  'scholarship_doc_desc_label': 'Document Description',
  'scholarship_btn_delete_permanently': 'Delete Permanently',
  'scholarship_error_invalid_student': 'Selected user is not a valid student.',
  'scholarship_warning_select_file': 'Please select a file first.',
  'scholarship_warning_enter_description': 'Please enter a document description.',
  'scholarship_info_doc_removed': 'Document removed. Click save to finalize.',
  'scholarship_error_select_student': 'Please select a student.',
  'scholarship_success_created': 'Application created successfully.',
  'scholarship_success_updated': 'Application updated successfully.',
  'scholarship_success_activated': 'Scholarship activated and applied to student account!',
  'scholarship_apps_reset_filters': 'Reset Filters',
  'scholarship_apps_apply_filters': 'Apply Filters',
  'scholarship_apps_table_empty_filtered': 'No applications found matching filters.',
  'scholarship_error_save_failed': 'Failed to save application.',
  'scholarship_success_deleted': 'Application deleted successfully.',
  'scholarship_error_delete_failed': 'Failed to delete application.',
  'scholarship_error_activate_failed': 'Failed to activate scholarship.',
  'scholarship_type_label': 'Scholarship Type*',
  'scholarship_benefit_type_label': 'Benefit Type*',
  'scholarship_benefit_value_label': 'Benefit Value*',
  'scholarship_benefit_percentage_label': 'Benefit Percentage*',
  'scholarship_benefit_value_placeholder_fixed': 'Enter fixed ETB amount',
  'scholarship_benefit_value_placeholder_percentage': 'Enter % value',
  'scholarship_description_label': 'Scholarship Description / Reason*',
  'scholarship_description_placeholder': 'Detailed reason for the award, specific conditions, or student circumstances...',
  'scholarship_benefit_tip': '** Tip: Set to 0 if "Full Fee Waiver" is selected.',
  'scholarship_assign_finance_staff': 'Assign Finance Staff',
  'scholarship_assign_finance_staff_desc': 'Assign this application to a specific finance staff member for review. You cannot assign it to yourself.',
  'scholarship_select_student_first': 'Select student first',
  'scholarship_staff_placeholder': 'Select Staff...',
  'scholarship_type_placeholder': 'Select Type...',
  'scholarship_benefit_type_placeholder': 'Select Benefit Type...',
  'scholarship_year_level_placeholder': 'Select Year...',
  'scholarship_semester_placeholder': 'Select Semester...',
  'scholarship_details_last_review': 'Last Review / Modification',
  'scholarship_details_pending': 'Pending',
  'scholarship_save_draft_btn': 'Save Draft',
  'scholarship_submit_approval_btn': 'Submit for Finance Approval',
  'scholarship_activate_btn': 'Activate Scholarship',
  'scholarship_delete_btn': 'Delete Application',
  'scholarship_btn_confirm_activate': 'Confirm & Activate',
  'scholarship_name_placeholder': 'e.g., Financial Hardship Grant for...',

  // Scholarship Finance Staff Labels
  'scholarship_finance_approvals_title': 'Scholarship Finance Approvals',
  'scholarship_finance_approvals_desc': 'The following applications have been submitted by an administrator and are awaiting your financial review.',
  'scholarship_finance_approvals_empty': 'No applications are currently awaiting financial review.',
  'scholarship_finance_col_student': 'Student Name',
  'scholarship_finance_col_department': 'Department',
  'scholarship_finance_col_scholarship': 'Scholarship',
  'scholarship_finance_col_date': 'Applied Date',
  'scholarship_finance_col_actions': 'Actions',
  'scholarship_finance_review_btn': 'Review',
  'scholarship_finance_review_title': 'Finance Review: {id}',
  'scholarship_finance_review_loading': 'Loading Application...',
  'scholarship_finance_not_pending': 'This application is no longer pending finance review.',
  'scholarship_finance_fetch_error': 'Failed to load application data.',
  'scholarship_finance_comments_required': 'Comments are required for rejection.',
  'scholarship_finance_review_success': 'Finance review submitted successfully.',
  'scholarship_finance_review_failed': 'Failed to submit review.',
  'scholarship_finance_detail_benefit_type': 'Benefit Type',
  'scholarship_finance_detail_benefit_value': 'Benefit Value',
  'scholarship_finance_detail_academic_term': 'Academic Term',
  'scholarship_finance_detail_reason': 'Reason / Description',
  'scholarship_finance_detail_for': 'For:',
  'scholarship_finance_detail_na': 'N/A',
  'scholarship_finance_documents_title': 'Supporting Documents',
  'scholarship_finance_no_documents': 'No documents were attached.',
  'scholarship_finance_document_label': 'Document',
  'scholarship_finance_panel_title': 'Finance Review Panel',
  'scholarship_finance_comments_label': 'Your Comments (Required for Rejection)',
  'scholarship_finance_comments_placeholder': 'Provide justification for your decision...',
  'scholarship_finance_reject_btn': 'Reject',
  'scholarship_finance_approve_btn': 'Approve',

  // Fee Structures Labels
  'fees_page_title': 'Fee Structures',
  'user_details_modal_title': 'User Profile: {name}',
  'user_details_core_info_header': 'Account Identity',
  'user_details_academic_header': 'Academic Vector',
  'user_details_financial_header': 'Financial Integrity',
  'user_details_term_fee_header': 'Current Period Ledger',
  'user_details_scholarships_header': 'Institutional Grants',
  'user_details_penalties_header': 'Audit Deviations',
  'user_details_notes_header': 'Forensic Audit Notes',
  'user_details_staff_header': 'Staff Authorization Profile',
  'user_details_no_fees_assigned': 'No fees have been assigned to this student yet.',
  'fees_subtitle': 'Access read-only repository of institutional fee structures. Use the advanced filtering engine below to analyze billing across departments, academic years, and semesters with forensic precision.',
  'fees_create_btn': 'Create Fee Structure',
  'fees_loading': 'Loading fee structures...',
  'fees_fetch_error': 'Failed to fetch fee structures.',
  'fees_table_col_name': 'Name',
  'fees_table_col_amount': 'Amount',
  'fees_table_col_status': 'Status',
  'fees_empty_structures': 'No fee structures found for the selected filters.',

  // Installment Plans Labels
  'installments_page_title': 'Installment Plan Registry',
  'installments_subtitle': 'Access read-only repository of institutional installment plan templates and active student assignments. Use the advanced filtering engine below to analyze repayment cycles and outstanding balances with forensic precision.',
  'installments_create_btn': 'Create Installment Plan',
  'installments_tabs_templates': 'Plan Templates',
  'installments_tabs_assignments': 'Active Assignments',
  'installments_loading': 'Loading installment plans...',
  'installments_fetch_error': 'Failed to fetch installment plans.',
  'installments_empty_assignments': 'No students are currently assigned to an installment plan.',
  'installments_empty_templates': 'No installment plan templates found.',

  // Security Center Page Labels
  'security_center_page_title': 'Security Center',
  'security_center_sessions_title': 'Active Sessions',
  'security_center_lockouts_title': 'Account Lockouts',
  'security_center_threats_title': 'Security Threats',
  'security_center_fetch_error': 'Failed to retrieve security data.',
  'security_center_update_success': 'Anomaly status updated successfully.',
  'security_center_update_error': 'Failed to update anomaly status.',
  'security_center_total_anomalies': 'Total Anomalies',
  'security_center_new_anomalies': 'New Anomalies',
  'security_center_new_text': 'New',
  'security_center_anomalies_type': 'Anomaly Types',
  'security_center_note_lifecycle': 'Secure Note Lifecycle',
  'security_center_pending_retrieval': 'Pending Retrieval',
  'security_center_consumed': 'Consumed (Burned)',
  'security_center_expired': 'Expired / Unread',
  'security_center_detected_anomalies': 'Detected Anomalies',

  // Security Anomaly Table Labels
  'security_col_type': 'Type',
  'security_col_severity': 'Severity',
  'security_col_status': 'Status',
  'security_col_detected': 'Detected At',
  'security_empty_anomalies': 'No security anomalies detected.',

  // Security Anomaly Collapsible Row Labels
  'security_anomaly_details_heading': 'Investigation Details',
  'security_anomaly_metadata_heading': 'Context Metadata',
  'security_anomaly_incident_id': 'Incident ID',
  'security_anomaly_detected_at': 'Detected At',
  'security_anomaly_related_user': 'Related User',
  'security_anomaly_unauthenticated': 'Unauthenticated',

  // Security Anomaly Detail Strategy Labels
  'security_detail_ip_address': 'IP Address',
  'security_detail_failed_attempts': 'Failed Attempts',
  'security_detail_location': 'Location',
  'security_detail_user_agent': 'User Agent',
  'security_detail_target_endpoint': 'Target Endpoint',
  'security_detail_malicious_payload': 'Malicious Payload',

  // System Governance Labels
  'sys_governance_page_title': 'System Governance',
  'sys_governance_policies_title': 'Governance Policies',
  'sys_governance_violations_title': 'Policy Violations',
  'sys_governance_loading': 'Loading governance data...',
  'dlq_page_title': 'Dead Letter Queues',
  'dlq_page_subtitle': 'Forensic management and surgical replay of failed background tasks.',

  // Queue Governance Labels
  'queue_gov_health_title': 'Queue Health Overview',
  'queue_gov_health_desc': 'Real-time telemetry and surgical management of institutional background task queues.',
  'queue_gov_loading': 'Loading queue metrics...',
  'queue_gov_fetch_error': 'Failed to retrieve queue metrics.',
  'queue_gov_col_queue': 'Queue Prefix',
  'queue_gov_col_active': 'Active',
  'queue_gov_col_waiting': 'Waiting',
  'queue_gov_col_retry': 'Retry (DLQ-Tier 1)',
  'queue_gov_col_dlq': 'Dead Letter (DLQ-Tier 2)',
  'queue_gov_col_throughput': 'Throughput (Msg/Sec)',
  'queue_gov_col_consumer': 'Consumer',
  'queue_gov_col_inflight': 'Inflight',
  'queue_gov_col_actions': 'Surgical Actions',
  'queue_gov_stat_total_queues': 'Managed Queues',
  'queue_gov_stat_dead_letters': 'Dead Letters',
  'queue_gov_stat_action_required': 'Action Required',
  'queue_gov_stat_worker_health': 'Worker Health',
  'queue_gov_stat_consumers': 'Consumer State',
  'queue_gov_stat_suspended': '{count} Suspended',
  'queue_gov_no_queues': 'No active queues discovered in the system.',
  'queue_gov_consumer_active': 'Active',
  'queue_gov_consumer_suspended': 'Suspended',
  'queue_gov_confirm_purge_title': 'Confirm Surgical Purge',
  'queue_gov_confirm_purge_desc': 'Are you sure you want to permanently purge all messages in the {tier} tier for {queue}? This action is irreversible.',
  'queue_gov_confirm_replay_title': 'Confirm Replay Operation',
  'queue_gov_confirm_replay_desc': 'Are you sure you want to forcefully replay messages from the Retry tier back into the main queue for {queue}?',
  'queue_gov_confirm_suspend_title': 'Suspend Consumer',
  'queue_gov_confirm_suspend_desc': 'This will halt message delivery for "{queue}". Messages will remain safely in the queue until the consumer is resumed.',
  'queue_gov_suspend_reason_label': 'Administrative Reason',
  'queue_gov_suspend_reason_placeholder': 'e.g. Investigating SMTP failures...',
  'queue_gov_confirm_quarantine_title': 'Quarantine Consumer',
  'queue_gov_quarantine_warning': 'This is an IRREVERSIBLE atomic operation. It will: (1) Suspend the consumer, (2) Drain all inflight messages, (3) Purge ALL queue tiers (Main, Retry, DLQ).',
  'queue_gov_confirm_quarantine_desc': 'All messages for "{queue}" will be permanently destroyed.',
  'queue_gov_quarantine_reason_label': 'Quarantine Justification',
  'queue_gov_quarantine_reason_placeholder': 'e.g. Poison messages causing cascading failures...',
  'queue_gov_action_replay': 'Replay',
  'queue_gov_action_replay_tip': 'Replay messages from Retry to Main',
  'queue_gov_action_purge_retry': 'Purge Retry',
  'queue_gov_action_purge_dlq': 'Purge DLQ',
  'queue_gov_action_suspend': 'Suspend',
  'queue_gov_action_resume': 'Resume',
  'queue_gov_action_quarantine': 'Quarantine',
  'queue_gov_action_quarantine_tip': 'Atomic: Suspend → Drain → Purge All Tiers',
  'queue_gov_replay_success': 'Retry tier replay initiated.',
  'queue_gov_purge_success': 'Queue tier purged successfully.',
  'queue_gov_suspend_success': 'Consumer suspended. Messages will queue safely.',
  'queue_gov_resume_success': 'Consumer resumed. Message delivery restarted.',
  'queue_gov_quarantine_success': 'Consumer quarantined. Drained {drained}, purged {purged} messages.',

  // Dead Letter Queue Labels
  'dlq_table_title': 'Dead Letter Management',
  'governance_dlq_sub': 'Failed background messages awaiting administrative review or replay.',
  'dlq_col_queue': 'Queue Name',
  'dlq_col_health': 'Worker Status',
  'dlq_col_depth': 'Messages',
  'dlq_col_actions': 'Actions',
  'dlq_action_peek': 'Inspect',
  'dlq_action_replay_all': 'Replay All',
  'dlq_action_replaying': 'Replaying...',
  'dlq_action_purge': 'Purge',
  'dlq_no_queues': 'No dead letters found.',
  'dlq_worker_healthy_tip': 'Worker is active and listening',
  'dlq_worker_unhealthy_tip': 'No active workers detected!',
  'dlq_inspector_title': 'Message Inspector',
  'dlq_inspector_desc': 'Analyzing raw message payloads from queue',
  'dlq_no_messages': 'No messages found in this queue.',
  'dlq_retries_label': 'retries',
  'dlq_last_error': 'Error',
  'dlq_payload_label': 'Raw Payload',
  'dlq_peek_limit_note': 'Showing last {limit} messages',
  'dlq_purge_confirm_title': 'Purge Dead Letter Queue',
  'dlq_purge_confirm_msg': 'Are you sure you want to permanently delete ALL messages from "{purgeTarget}"? This action is irreversible.',
  'dlq_purge_confirm_btn': 'Purge All',
  'dlq_replay_success': 'Replayed {replayedCount} messages successfully.',
  'dlq_purge_success': 'Purged {purgedCount} messages.',

  // Common Governance Labels
  'common_live_telemetry': 'Live Telemetry',
  'common_all_healthy': 'All Active',
  'common_all_active': 'All Consuming',

  // Governance Management Labels
  'governance_col_category': 'Category',
  'governance_col_policy_name': 'Policy Name',
  'governance_col_status': 'Status',
  'governance_policy_technical_config': 'Technical Parameters Configuration',
  'governance_policy_editing_mode': 'Editing Mode',
  'governance_no_policies': 'No active governance policies found.',
  'governance_confirm_title': 'Administrative Confirmation',
  'governance_confirm_msg_parameters': 'Are you sure you want to update the parameters for this policy?',
  'governance_confirm_msg_toggle': 'Are you sure you want to {action} the "{policy}" policy?',
  'governance_action_modify': 'Modify Config',
  'governance_action_apply': 'Apply Changes',
  'governance_col_infraction': 'Policy Infraction',
  'governance_col_detected_at': 'Detected At',
  'governance_col_current_status': 'Current Status',
  'governance_col_admin_action': 'Administrative Action',
  'governance_no_violations': 'No governance violations detected. System is in compliance.',
  'governance_violation_forensic_details': 'Violation Forensic Details',
  'governance_violation_policy_context': 'Policy Context:',
  'governance_confirm_resolution_title': 'Administrative Resolution Confirmation',
  'governance_confirm_resolution_msg': 'Are you sure you want to change the status of this violation to {status}?',
  'governance_confirm_resolution_btn': 'Confirm Status Update',

  // Feedback Management Labels
  'feedback_mgmt_page_title': 'Feedback Management',
  'feedback_mgmt_subtitle': 'Monitor and manage the event bus outbox and notification delivery logs.',
  'feedback_mgmt_loading': 'Loading feedback...',
  'feedback_mgmt_fetch_error': 'Failed to fetch feedback.',
  'feedback_mgmt_filter_title': 'Filter Feedback',
  'feedback_mgmt_search_placeholder': 'Search feedback...',
  'feedback_mgmt_status_filter': 'Status',
  'feedback_mgmt_priority_filter': 'Priority',
  'feedback_mgmt_category_filter': 'Category',
  'feedback_mgmt_department_filter': 'Department',
  'feedback_mgmt_date_from': 'Date From',
  'feedback_mgmt_date_to': 'Date To',
  'feedback_mgmt_assigned_to': 'Assigned To',
  'feedback_mgmt_apply_filters': 'Apply Filters',
  'feedback_mgmt_reset_filters': 'Reset',
  'feedback_mgmt_table_col_id': 'ID',
  'feedback_mgmt_table_col_subject': 'Subject',
  'feedback_mgmt_table_col_status': 'Status',
  'feedback_mgmt_table_col_priority': 'Priority',
  'feedback_mgmt_table_col_category': 'Category',
  'feedback_mgmt_table_col_created': 'Created',
  'feedback_mgmt_table_col_actions': 'Actions',
  'feedback_mgmt_view_details': 'View Details',
  'feedback_mgmt_delete': 'Delete',
  'feedback_mgmt_assign': 'Assign',
  'feedback_mgmt_assign_modal_title': 'Assign Feedback',
  'feedback_mgmt_assign_to_label': 'Assign to:',
  'feedback_mgmt_assign_success': 'Feedback assigned successfully.',
  'feedback_mgmt_assign_error': 'Failed to assign feedback.',
  'feedback_mgmt_delete_confirm': 'Are you sure you want to delete this feedback?',
  'feedback_mgmt_delete_success': 'Feedback deleted successfully.',
  'feedback_mgmt_delete_error': 'Failed to delete feedback.',
  'feedback_mgmt_total_title': 'Total Feedback',
  'feedback_mgmt_open_title': 'Open',
  'feedback_mgmt_in_progress_title': 'In Progress',
  'feedback_mgmt_resolved_title': 'Resolved',

  // Feedback Details Page Labels
  'feedback_details_page_title': 'Feedback Details',
  'feedback_details_loading': 'Loading feedback details...',
  'feedback_details_fetch_error': 'Failed to fetch feedback details.',
  'feedback_details_back_btn': 'Back to Feedback',
  'feedback_details_subject_label': 'Subject',
  'feedback_details_status_label': 'Status',
  'feedback_details_priority_label': 'Priority',
  'feedback_details_category_label': 'Category',
  'feedback_details_description_label': 'Description',
  'feedback_details_replies_title': 'Replies',
  'feedback_details_no_replies': 'No replies yet.',
  'feedback_details_reply_placeholder': 'Write a reply...',
  'feedback_details_send_reply': 'Send Reply',
  'feedback_details_save_changes': 'Save Changes',
  'feedback_details_update_success': 'Feedback updated successfully.',
  'feedback_details_update_error': 'Failed to update feedback.',
  'feedback_details_reply_success': 'Reply sent successfully.',
  'feedback_details_reply_error': 'Failed to send reply.',

  // Navigation Settings Labels
  'nav_settings_page_title': 'Navigation Settings',
  'nav_settings_subtitle': 'Configure navigation menus and links for different user roles.',
  'nav_settings_loading': 'Loading navigation settings...',
  'nav_settings_fetch_error': 'Failed to fetch navigation settings.',
  'nav_settings_role_tabs_title': 'Role Navigation',
  'nav_settings_total_links': 'Total Links',
  'nav_settings_active_links': 'Active Links',
  'nav_settings_inactive_links': 'Inactive Links',
  'nav_settings_add_link': 'Add Link',
  'nav_settings_edit_link': 'Edit Link',
  'nav_settings_delete_link': 'Delete Link',
  'nav_settings_link_name': 'Link Name',
  'nav_settings_link_path': 'Path',
  'nav_settings_link_icon': 'Icon',
  'nav_settings_link_parent': 'Parent',
  'nav_settings_link_order': 'Order',
  'nav_settings_link_enabled': 'Enabled',
  'nav_settings_save_btn': 'Save',
  'nav_settings_cancel_btn': 'Cancel',
  'nav_settings_create_success': 'Navigation link created successfully.',
  'nav_settings_update_success': 'Navigation link updated successfully.',
  'nav_settings_delete_success': 'Navigation link deleted successfully.',
  'nav_settings_delete_confirm': 'Are you sure you want to delete this navigation link?',
  'nav_settings_refresh_btn': 'Refresh',

  // Dynamic Config Page Labels
  'dynamic_config_page_title': 'Dynamic Configuration',
  'dynamic_config_loading': 'Loading configuration...',
  'dynamic_config_fetch_error': 'Failed to fetch configuration.',
  'dynamic_config_tab_ui_schema': 'UI Schema',
  'dynamic_config_tab_table_config': 'Table Configuration',
  'dynamic_config_save_btn': 'Save Configuration',
  'dynamic_config_save_success': 'Configuration saved successfully.',
  'dynamic_config_save_error': 'Failed to save configuration.',
  'dynamic_config_reset_btn': 'Reset to Defaults',

  // Workflow Monitor Labels
  'workflow_monitor_page_title': 'Workflow Monitor',
  'workflow_monitor_subtitle': 'Monitor and manage workflow instances and their execution status.',
  'workflow_monitor_loading': 'Loading workflows...',
  'workflow_monitor_fetch_error': 'Failed to fetch workflows.',
  'workflow_monitor_filter_title': 'Filter Workflows',
  'workflow_monitor_status_filter': 'Status',
  'workflow_monitor_type_filter': 'Workflow Type',
  'workflow_monitor_date_from': 'Date From',
  'workflow_monitor_date_to': 'Date To',
  'workflow_monitor_search_placeholder': 'Search by ID or entity...',
  'workflow_monitor_apply_filters': 'Apply',
  'workflow_monitor_clear_filters': 'Clear',
  'workflow_monitor_refresh_btn': 'Refresh',
  'workflow_monitor_table_col_id': 'Workflow ID',
  'workflow_monitor_table_col_type': 'Type',
  'workflow_monitor_table_col_status': 'Status',
  'workflow_monitor_table_col_created': 'Created',
  'workflow_monitor_table_col_updated': 'Updated',
  'workflow_monitor_table_col_actions': 'Actions',
  'workflow_monitor_view_details': 'View Details',
  'workflow_monitor_retry_btn': 'Retry',
  'workflow_monitor_cancel_btn': 'Cancel',
  'workflow_monitor_retry_confirm': 'Are you sure you want to retry this workflow?',
  'workflow_monitor_retry_success': 'Workflow retry initiated.',
  'workflow_monitor_retry_error': 'Failed to retry workflow.',
  'workflow_monitor_cancel_confirm': 'Are you sure you want to cancel this workflow?',
  'workflow_monitor_cancel_success': 'Workflow cancelled.',
  'workflow_monitor_cancel_error': 'Failed to cancel workflow.',
  'workflow_monitor_details_title': 'Workflow Details',
  'workflow_monitor_details_close': 'Close',
  'workflow_monitor_stats_total': 'Total Workflows',
  'workflow_monitor_stats_pending': 'Pending',
  'workflow_monitor_stats_running': 'Running',
  'workflow_monitor_stats_completed': 'Completed',
  'workflow_monitor_stats_failed': 'Failed',

  // Notification Management Labels
  'notif_mgmt_page_title': 'Notification Management',
  'notif_mgmt_subtitle': 'Monitor and manage the event bus outbox and notification delivery logs.',
  'notif_mgmt_tab_analytics': 'Analytics',
  'notif_mgmt_tab_outbox': 'Event Outbox',
  'notif_mgmt_tab_logs': 'Delivery Logs',
  'notif_mgmt_loading': 'Loading notifications...',
  'notif_mgmt_fetch_error': 'Failed to fetch notifications.',
  'notif_mgmt_outbox_retry': 'Retry',
  'notif_mgmt_outbox_retry_success': 'Message retry initiated.',
  'notif_mgmt_outbox_retry_error': 'Failed to retry message.',
  'notif_mgmt_logs_resubmit': 'Resubmit',
  'notif_mgmt_logs_resubmit_success': 'Notification resubmission initiated.',
  'notif_mgmt_logs_resubmit_error': 'Failed to resubmit notification.',

  // Observability Dashboard Labels
  'observability_page_title': 'Observability Dashboard',
  'observability_loading': 'Loading metrics...',
  'observability_fetch_error': 'Failed to fetch observability metrics.',
  'observability_health_title': 'System Health Overview',
  'observability_api_requests': 'Total API Requests',
  'observability_api_errors': 'Total API Errors',
  'observability_comms_sent': 'Total Comms Sent',
  'observability_comms_failed': 'Total Comms Failed',
  'observability_latency_chart_title': 'API Request Latency (seconds)',
  'observability_chart_placeholder': 'Another chart coming soon...',

  // Payment Options Page Labels
  'payment_options_page_title': 'Payment Options',
  'payment_options_loading': 'Loading payment gateways...',
  'payment_options_fetch_error': 'Failed to fetch payment gateways.',
  'payment_options_gateway_title': 'Payment Gateway Management',
  'payment_options_enabled': 'Enabled',
  'payment_options_disabled': 'Disabled',
  'payment_options_update_success': 'Gateway updated successfully.',
  'payment_gateway_status_enabled': 'Enabled',
  'payment_gateway_status_disabled': 'Disabled',
  'payment_options_update_error': 'Failed to update gateway.',
  'payment_options_no_gateways': 'No payment gateways found.',
  'payment_options_show_field': 'Show',
  'payment_options_hide_field': 'Hide',

  // JIT Access Console Labels
  'jit_access_page_title': 'JIT Access Console',
  'jit_access_loading': 'Loading JIT data...',
  'jit_access_fetch_error': 'Failed to fetch JIT data.',
  'jit_access_grant_title': 'Grant Temporary Permission',
  'jit_access_active_title': 'Active Temporary Permissions',
  'jit_access_user_label': 'User',
  'jit_access_permission_label': 'Permission',
  'jit_access_duration_label': 'Duration (minutes)',
  'jit_access_reason_label': 'Reason',
  'jit_access_grant_btn': 'Grant Permission',
  'jit_access_revoke_btn': 'Revoke',
  'jit_access_grant_success': 'Permission granted successfully.',
  'jit_access_grant_error': 'Failed to grant permission.',
  'jit_access_revoke_confirm': 'Are you sure you want to revoke this temporary permission?',
  'jit_access_revoke_success': 'Permission revoked successfully.',
  'jit_access_revoke_error': 'Failed to revoke permission.',
  'jit_access_loading_perms': 'Loading active permissions...',
  'jit_access_table_col_user': 'User',
  'jit_access_table_col_permission': 'Permission',
  'jit_access_table_col_expires': 'Expires At',
  'jit_access_table_col_granted_by': 'Granted By',
  'jit_access_table_col_actions': 'Actions',

  // System Reports & Logs Labels
  'reports_logs_page_title': 'System Reports & Logs',
  'reports_logs_tab_financial': 'Financial Reports',
  'reports_logs_tab_activity': 'User Activity',
  'reports_logs_tab_security': 'Security Audits',
  'reports_logs_tab_errors': 'System Errors',
  'reports_logs_tab_notifications': 'Notifications',
  'reports_logs_tab_jobs': 'Job Queue',
  'reports_logs_loading': 'Loading reports...',
  'reports_logs_fetch_error': 'Failed to fetch reports.',

  // STUDENT PORTAL LABELS

  // Student Portal - Dashboard
  'student_dashboard_welcome_back': 'Welcome back, {name}',
  'student_dashboard_student_id': 'Student ID',
  'student_dashboard_loading_error': 'Loading Data Error',
  'student_dashboard_payment_reminder': 'Payment Due Reminder',
  'student_dashboard_payment_due_msg': 'Your payment for {term} is due by {deadline}.',
  'student_dashboard_try_again': 'Try Again',
  'student_dashboard_fee_overview': 'Fee Overview - {term}',
  'student_dashboard_financial_health': 'Financial Health',
  'student_dashboard_total_fee_help': 'Base tuition and fees before adjustments.',
  'student_dashboard_scholarships_title': 'Scholarships & Grants',
  'student_dashboard_scholarships_help': 'Total scholarship amount deducted from fees.',
  'student_dashboard_adjusted_fee_title': 'Adjusted Fee Due',
  'student_dashboard_adjusted_fee_help': 'Net fee amount after scholarships and subsidies.',
  'student_dashboard_amount_paid_title': 'Amount Paid So Far',
  'student_dashboard_amount_paid_help': 'Total payments successfully processed.',
  'student_dashboard_outstanding_help': 'Current remaining amount due.',
  'student_dashboard_deadline_title': 'Payment Deadline',
  'student_dashboard_deadline_help': 'Final date to clear outstanding balance for this term.',
  'student_dashboard_fees_cleared': 'All Fees Cleared!',
  'student_dashboard_fees_covered': 'Your account is currently in good standing or fully covered by scholarships.',
  'student_dashboard_current_payment_status': 'Current Payment Status',

  // Student Dashboard - Financial Status Mappings
  'student_dashboard_status_paid_full_scholarship': 'Paid (Full Scholarship)',
  'student_dashboard_status_paid_in_full': 'Paid in Full',
  'student_dashboard_status_fee_not_assigned': 'Fee Not Assigned',
  'student_dashboard_status_outstanding': 'Outstanding: {amount} {currency}',
  'student_dashboard_status_outstanding_generic': 'Outstanding Balance',
  'student_dashboard_status_overdue': 'Overdue: {amount} {currency}',
  'student_dashboard_status_overdue_generic': 'Overdue',

  // Financial Health Mappings
  'student_dashboard_financial_health_EXCELLENT': 'Excellent',
  'student_dashboard_financial_health_GOOD': 'Good',
  'student_dashboard_financial_health_WARNING': 'Warning',
  'student_dashboard_financial_health_CRITICAL': 'Critical',
  'common_days_overdue': 'Days Overdue',
  'common_days_left': 'Days Left',

  // Student Portal - Payment Labels
  'student_payment_page_header': 'Make a Payment',
  'student_payment_for_user': 'Payment for {name}',
  'student_payment_outstanding_balance': 'Outstanding Balance',
  'student_payment_option_legend': 'Payment Option',
  'student_payment_option_full': 'Pay in Full',
  'student_payment_option_installment': 'Pay in Installments',
  'student_payment_amount_to_pay': 'Amount to Pay',
  'student_payment_phone_label': 'Phone Number',
  'student_payment_phone_hint': '(Used for mobile money)',
  'student_payment_phone_placeholder': 'e.g., 0911223344',
  'student_payment_method_legend': 'Payment Method',
  'student_payment_btn_proceed': 'Proceed to Payment',
  'student_payment_loading_error': 'Failed to load payment information.',
  'student_payment_all_cleared': 'All Fees Cleared!',
  'student_payment_cleared_message': 'Your account has no outstanding fees for this term.',
  'student_payment_return_dashboard': 'Return to Dashboard',
  'student_payment_next_installment_title': 'Next Installment',
  'student_payment_next_installment_desc': 'Installment #{number} of {plan}',
  'student_payment_due_date': 'Due Date: {date}',
  'student_payments_make_payment_submit_loading': 'Initiating...',
  'student_payment_btn_pay_installment': 'Pay Installment',
  'student_payment_no_plans': 'No installment plans available for your current fee structure.',
  'student_payment_plan_label': 'Select Installment Plan',
  'student_payment_plan_placeholder': 'Choose a plan...',
  'student_payment_confirm_title': 'Confirm Payment',
  'student_payment_confirm_desc': 'You are about to pay {amount} using {gateway}. Click Confirm to proceed.',
  'student_payment_btn_cancel': 'Cancel',
  'student_payment_confirm_redirecting': 'Redirecting...',
  'student_payment_btn_confirm': 'Confirm & Pay',
  'student_payment_success': 'Payment initiated successfully.',
  'student_payment_error': 'Failed to initiate payment. Please try again.',
  'student_payment_active_plan_badge': 'Active Plan',
  'config_payment_gateway_manual_key': 'MANUAL',

  // Student Scholarship Grants Page
  'student_grants_page_title': 'My Scholarships',
  'student_grants_page_subtitle': 'Active and historical scholarship grants applied to your account.',
  'student_grants_empty_title': 'No Scholarships Found',
  'student_grants_empty_desc': 'There are no scholarship grants associated with your academic profile at this time.',
  'student_grants_benefit_amount': 'Benefit Amount',
  'student_grants_academic_year': 'Academic Year',
  'student_grants_status': 'Status',
  'student_grants_application_date': 'Award Date',
  'student_grants_provider': 'Provider/Sponsor',
  'student_grants_overview_title': 'Scholarships Overview',
  'student_grants_active_count': 'Active Grants',
  'student_grants_total_value': 'Total Value',
  'student_grants_filter_title': 'Filter Grants',
  'student_grants_all_statuses': 'All Statuses',
  'student_grants_col_id': 'Grant ID',
  'student_grants_col_name': 'Scholarship Name',
  'student_grants_col_benefit': 'Benefit Type',
  'student_grants_col_amount': 'Amount',
  'student_grants_col_date': 'Award Date',
  'student_grants_col_status': 'Status',

  // Scholarship Grant Detail View
  'scholarship_academic_year': 'Academic Year',
  'scholarship_semester': 'Semester',
  'scholarship_benefit_type': 'Benefit Type',
  'scholarship_benefit_value': 'Benefit Value',
  'scholarship_description_title': 'Description',
  'scholarship_timeline_title': 'Application Timeline',
  'scholarship_grant_details_modal_title': 'Grant Details Inspector',
  'errors_load_scholarship_grant_details': 'Failed to load scholarship details.',

  // Common Labels
  'common_action': 'Action',
  'common_view_details': 'View Details',

  // Scholarship Statuses & Actions
  'PENDING': 'Pending',
  'SUBMITTED': 'Submitted',
  'APPROVED': 'Approved',
  'REJECTED': 'Rejected',
  'ACTIVE': 'Active',
  'COMPLETED': 'Completed',
  'PENDING_FINANCE_APPROVAL': 'Pending Finance Approval',
  'APPROVED_BY_FINANCE': 'Approved by Finance',
  'REJECTED_BY_FINANCE': 'Rejected by Finance',
  'APPROVED_BY_FINANCE.': 'Approved by Finance',
  'APPROVED_BY_SCHOLARSHIP_COMMITTEE': 'Approved by Committee',
  'REJECTED_BY_SCHOLARSHIP_COMMITTEE': 'Rejected by Committee',
  'FULL_FEE_WAIVER': 'Full Fee Waiver',
  'INSTITUTIONAL': 'Institutional',
  'GOVERNMENT': 'Government',
  'PRIVATE': 'Private',
  'PERCENTAGE': 'Percentage',
  'FIXED_AMOUNT': 'Fixed Amount',

  // Student Portal - Profile Page
  'student_profile_page_title': 'My Profile & Settings',
  'student_profile_header': 'My Profile Settings',
  'student_profile_account_title': 'My Account Profile & Settings',
  'student_profile_tab_info': 'Personal Info',
  'student_profile_tab_password': 'Password',
  'student_profile_tab_notifications': 'Notifications',
  'student_profile_tab_2fa': '2FA',
  'student_profile_tab_privacy': 'Privacy',
  'student_profile_tab_info_desc': 'Manage your personal information preferences and security.',
  'student_profile_tab_password_desc': 'Manage your password preferences and security.',
  'student_profile_tab_notifications_desc': 'Manage your notification preferences and security.',
  'student_profile_tab_2fa_desc': 'Manage your 2FA preferences and security.',
  'student_profile_tab_privacy_desc': 'Manage your privacy preferences and security.',
  'student_profile_settings_suffix': 'Settings',
  'student_profile_manage_prefs': 'Manage your {tab} preferences and security.',
  'student_profile_updated_success': 'Profile updated successfully.',
  'student_password_mismatch': 'New passwords do not match.',
  'student_password_updated_success': 'Password updated successfully. Logging out...',

  // Student Portal - Personal Info Tab
  'student_info_title': 'Personal Information',
  'student_info_id_label': 'Student ID',
  'student_info_username_label': 'Username',
  'student_info_readonly_suffix': '(Read-only)',
  'student_info_fullname_label': 'Full Name*',
  'student_info_email_label': 'Email Address*',
  'student_info_phone_label': 'Phone Number',
  'student_info_save_btn': 'Save Changes',
  'student_info_na': 'N/A',

  // Student Portal - Change Password Tab
  'student_password_title': 'Change Password',
  'student_password_current_label': 'Current Password*',
  'student_password_new_label': 'New Password*',
  'student_password_confirm_label': 'Confirm New Password*',
  'student_password_mismatch_error': 'New passwords do not match.',
  'student_password_update_btn': 'Update Password',
  'student_password_success': 'Password updated successfully. Please log in again.',

  // Student Portal - 2FA Tab
  'student_2fa_title': 'Two-Factor Authentication (2FA)',
  'student_2fa_enabled_msg': '2FA is currently ENABLED on your account.',
  'student_2fa_protected_msg': 'Your account is protected with an additional layer of security.',
  'student_2fa_disable_btn': 'Disable 2FA',
  'student_2fa_disabling': 'Disabling...',
  'student_2fa_intro': 'Enhance your account security by enabling 2FA. You will need an authenticator app (like Google Authenticator, Authy, or 1Password) on your phone.',
  'student_2fa_enable_btn': 'Enable 2FA',
  'student_2fa_finalize_title': 'Finalize 2FA Setup',
  'student_2fa_step1': 'Step 1:',
  'student_2fa_scan_qr': 'Scan this QR code with your authenticator app.',
  'student_2fa_manual_entry': 'Or, enter this secret key manually:',
  'student_2fa_step2': 'Step 2:',
  'student_2fa_enter_code': 'Enter the 6-digit code from your app to verify.',
  'student_2fa_verify_btn': 'Verify & Enable',
  'student_2fa_verifying': 'Verifying...',
  'student_2fa_enabled_success': 'Two-Factor Authentication enabled successfully.',
  'student_2fa_disabled_success': 'Two-Factor Authentication disabled successfully.',
  'student_2fa_disable_confirm': 'Are you sure you want to disable 2FA? This will reduce your account security.',


  // Profile Errors
  'errors_fetch_profile': 'Failed to fetch profile settings.',
  'errors_update_profile': 'Failed to update personal information.',
  'errors_update_password': 'Failed to change password. Please verify current password.',
  'errors_2fa_setup': 'Failed to initiate 2FA setup.',
  'errors_2fa_verify': 'Verification failed. Please check the code.',
  'errors_2fa_disable': 'Failed to disable 2FA.',
  'errors_export_data': 'Failed to export your data. Please try again later or contact support.',
  'errors_delete_account': 'Failed to process account deletion request.',

  // Student Portal - Privacy Tab
  'student_privacy_title': 'Data Privacy & Rights',
  'student_privacy_intro': 'Manage your personal data and exercise your rights under GDPR/CCPA.',
  'student_privacy_export_title': 'Export Data',
  'student_privacy_export_desc': 'Request a copy of all personal data we hold about you. You will receive a file via email.',
  'student_privacy_export_btn': 'Export My Data',
  'student_privacy_exporting': 'Requesting...',
  'student_privacy_export_success': 'Your data has been successfully exported and downloaded.',
  'student_privacy_danger_title': 'Danger Zone',
  'student_privacy_delete_desc': 'Permanently delete your account and all associated data. This action cannot be undone.',
  'student_privacy_delete_btn': 'Delete Account',
  'student_privacy_deleting': 'Processing...',
  'student_privacy_delete_confirm': 'Are you sure you want to delete your account? This action is irreversible and will sign you out immediately.',
  'student_privacy_delete_success': 'Account deletion request submitted.',

  // Student Portal - Notifications Page
  'student_notifications_page_title': 'Student Notifications',
  'student_notifications_title': 'Notifications',
  'student_notifications_total_msg': 'You have {count} notification(s) in total.',
  'student_notifications_empty_msg': 'Stay updated with your latest academic and financial alerts.',
  'student_notifications_mark_all_read': 'Mark all as read',
  'student_notifications_status_filter': 'Status',
  'student_notifications_all_statuses': 'All Statuses',
  'student_notifications_unread_only': 'Unread Only',
  'student_notifications_read_only': 'Read Only',
  'student_notifications_type_filter': 'Category',
  'student_notifications_all_categories': 'All Categories',
  'student_notifications_clear_filters': 'Clear Filters',
  'student_notifications_col_type': 'Type',
  'student_notifications_col_subject': 'Subject',
  'student_notifications_col_timestamp': 'Timestamp',
  'student_notifications_col_status': 'Status',
  'student_notifications_col_actions': 'Actions',
  'student_notifications_status_unread': 'Unread',
  'student_notifications_status_read': 'Read',
  'student_notifications_no_title': 'No Notifications Found',
  'student_notifications_no_desc': 'Stay tuned! Your academic alerts, payment confirmations, and institutional announcements will appear here as they are generated.',
  'student_notifications_modal_category': 'Category',
  'student_notifications_modal_received': 'Received',
  'student_notifications_action_link': 'Follow Action Link',
  'student_notifications_delete_btn': 'Delete Notification',
  'student_notifications_close_btn': 'Close',
  'student_notifications_delete_confirm': 'Confirm permanent deletion of this notification?',
  'student_notifications_deleted_success': 'Notification removed successfully.',
  'student_notifications_all_read_success': 'All items marked as read.',

  // Student Portal - Submit Feedback Page
  'student_feedback_page_title': 'Submit Feedback',
  'student_feedback_title': 'Submit Feedback, Suggestion, or Inquiry',
  'student_feedback_intro': 'We value your input. Please provide as much detail as possible.',
  'student_feedback_your_info': 'Your Information',
  'student_feedback_name_label': 'Your Name',
  'student_feedback_id_label': 'Student ID',
  'student_feedback_dept_label': 'Your Department*',
  'student_feedback_dept_placeholder': 'Select Department',
  'student_feedback_select_dept_placeholder': 'Select your department...',
  'student_feedback_phone_label': 'Your Phone Number*',
  'student_feedback_phone_placeholder': '0912345678',
  'student_feedback_content_legend': 'Feedback Content',
  'student_feedback_category_label': 'Feedback Category*',
  'student_feedback_category_placeholder': 'Select a category...',
  'student_feedback_subject_label': 'Subject (Optional)',
  'student_feedback_subject_placeholder': 'e.g., Question about my recent payment',
  'student_feedback_comment_label': 'Your Comment / Description*',
  'student_feedback_comment_placeholder': 'Please provide as much detail as possible...',
  'student_feedback_attachment_label': 'Attach File (Optional)',
  'student_feedback_attachment_hint': 'Max 5MB. Allowed: PDF, DOC, DOCX, JPG, PNG.',
  'student_feedback_file_info': 'Max 5MB. Allowed: PDF, DOC, DOCX, JPG, PNG.',
  'student_feedback_file_selected': 'File selected:',
  'student_feedback_submit_btn': 'Submit Feedback',
  'student_feedback_submitting': 'Submitting...',
  'student_feedback_success': 'Feedback submitted successfully! Reference ID: {id}',
  'student_feedback_error_dept': 'Please select your department.',
  'student_feedback_error_phone': 'Please enter your phone number.',
  'student_feedback_error_phone_format': 'Invalid phone number format. Use 09... or +2519...',
  'student_feedback_error_category': 'Please select a feedback category.',
  'student_feedback_error_comment': 'Comment/Issue field cannot be empty.',
  'student_feedback_error_file_size': 'File size cannot exceed 5MB.',

  // Student Portal - Download Receipts Page
  'student_receipts_page_title': 'My Payment Receipts',
  'student_receipts_header': 'My Payment Receipts',
  'student_receipts_search_placeholder': 'Search description/ID...',
  'student_receipts_from_label': 'From',
  'student_receipts_to_label': 'To',
  'student_receipts_col_date': 'Date',
  'student_receipts_col_desc': 'Description',
  'student_receipts_col_amount': 'Amount',
  'student_receipts_col_method': 'Method',
  'student_receipts_col_txn_id': 'Transaction ID',
  'student_receipts_download_btn': 'Download PDF',
  'student_receipts_preparing': 'Preparing...',
  'student_receipts_no_title': 'No Receipts Found',
  'student_receipts_no_desc': 'Once you have made successful payments, your downloadable official receipts will be archived here.',
  'student_receipts_load_error': 'Failed to load receipts. Please try again.',
  'student_receipts_download_error': 'Could not download the receipt. Please try again.',

  // Student Portal - Installment Schedule Page
  'student_installment_page_title': 'Installment Schedule',
  'student_installment_header': 'My Installment Schedule',
  'student_installment_not_applicable': 'Installment Plan Not Applicable',
  'student_installment_scholarship_funded': 'Your account is fully funded by a scholarship for the current term, so an installment plan is not required.',
  'student_installment_no_plan': 'No Active Plan',
  'student_installment_no_plan_desc': 'You do not currently have an active installment plan.',
  'student_installment_total_on_plan': 'Total on Plan',
  'student_installment_next_due_date': 'Next Due Date',
  'student_installment_next_amount': 'Next Amount',
  'student_installment_outstanding': 'Outstanding',
  'student_installment_col_due_date': 'Due Date',
  'student_installment_col_amount_due': 'Amount Due',
  'student_installment_col_amount_paid': 'Amount Paid',
  'student_installment_col_penalty': 'Penalty',
  'student_installment_col_status': 'Status',
  'student_installment_col_payment_date': 'Payment Date',
  'student_installment_col_action': 'Action',
  'student_installment_paid': 'Paid',
  'student_installment_make_payment': 'Make a Payment',
  'student_installment_return_dashboard': 'Return to Dashboard',
  'student_installment_na': 'N/A',
  'student_installment_load_error': 'Failed to fetch installment schedule.',

  // Student Portal - Payment Status Page
  'student_payment_status_page_title': 'Payment Status',
  'student_payment_status_verifying': 'Verifying Payment...',
  'student_payment_status_verifying_desc': 'Please wait while we confirm your transaction with the payment gateway.',
  'student_payment_status_submitted': 'Payment Submitted!',
  'student_payment_status_submitted_desc': 'Your transaction has been sent for processing. A final verification will happen automatically in the background. Please check your notifications for a confirmation message shortly.',
  'student_payment_status_failed': 'Verification Failed',
  'student_payment_status_failed_desc': 'Unfortunately, we could not verify your payment. Please contact support if you believe this is an error.',
  'student_payment_status_txn_ref': 'Transaction Reference:',
  'student_payment_status_view_history': 'View Payment History',
  'student_payment_status_return_dashboard': 'Return to Dashboard',
  'student_payment_status_try_again': 'Try Again',
  'student_payment_status_no_ref': 'No transaction reference found in the URL. Cannot verify payment.',
  'student_payment_status_verify_failed': 'Payment verification failed. Please contact support.',

  // --- BATCH 6: Navigation & Layout ---

  // Student Portal - Navigation Fallbacks (Legacy/Offline)
  'nav_student_dashboard': 'Dashboard',
  'nav_student_profile': 'Profile',
  'nav_student_make_payment': 'Make Payment',
  'nav_student_payment_status': 'Payment Status',
  'nav_student_payment_history': 'Payment History',
  'nav_student_installment_schedule': 'Installment Schedule',
  'nav_student_download_receipts': 'Download Receipts',
  'nav_student_notifications': 'Notifications',
  'nav_student_submit_feedback': 'Submit Feedback',

  // Header & Notifications UI
  'header_my_profile': 'My Profile',
  'header_logout': 'Logout',
  'header_notifications_title': 'Notifications',
  'header_notifications_empty': 'No new notifications.',
  'header_mark_all_read': 'Mark all as read',
  'header_mark_as_read': 'Mark as read',

  // Student Portal - Payment History Page
  'student_payment_history_title': 'Payment History',
  'student_payment_history_overview_title': 'Financial Overview',
  'student_payment_history_header': 'My Payment History',
  'student_payment_history_outstanding': 'Outstanding Balance',
  'student_payment_history_term': 'Academic Term',
  'student_payment_history_filter_title': 'Filter History',
  'student_payment_history_all_terms': 'All Academic Terms',
  'student_payment_history_all_statuses': 'All Statuses',
  'student_payment_history_all_methods': 'All Methods',
  'student_payment_history_col_txn_id': 'Transaction ID',
  'student_payment_history_col_date': 'Date',
  'student_payment_history_col_desc': 'Description',
  'student_payment_history_col_amount': 'Amount',
  'student_payment_history_col_method': 'Method',
  'student_payment_history_col_status': 'Status',
  'student_payment_history_col_actions': 'Actions',
  'student_payment_history_no_matches_title': 'No Matches Found',
  'student_payment_history_no_matches_desc': "We couldn't find any transactions matching your current filters. Try adjusting your selection or clearing all filters to see all payments.",
  'student_payment_history_empty_title': 'No Payment History Recorded',
  'student_payment_history_empty_desc': 'No payments have been recorded yet. Once you make a payment, your complete transaction history and digital receipts will be available here.',
  'student_payment_history_empty_with_balance': 'You have an outstanding balance of ETB {amount}. Your payment history will appear here once you complete your first transaction.',
  'student_payment_history_clear_filters': 'Clear All Filters',
  'student_payment_history_make_first_payment': 'Make Your First Payment',
  'student_payment_history_details_title': 'Payment Details',
  'student_payment_history_col_details': 'Details',
  'student_payment_history_id_label': 'Payment ID',
  'student_payment_history_gateway_txn_label': 'Gateway Transaction ID',
  'student_payment_history_receipt_id_label': 'Receipt ID',
  'student_payment_history_date_label': 'Payment Date',
  'student_payment_history_amount_label': 'Amount Paid',
  'student_payment_history_expected_label': 'Expected Amount',
  'student_payment_history_discrepancy_label': 'Discrepancy',
  'student_payment_history_method_label': 'Payment Method',
  'student_payment_history_type_label': 'Payment Type',
  'student_payment_history_type_installment': 'Installment Plan',
  'student_payment_history_type_full': 'Full Payment',
  'student_payment_history_status_label': 'Status',
  'student_payment_history_desc_label': 'Description',
  'student_payment_history_btn_close': 'Close',
  'student_payment_history_btn_download': 'Download Receipt (PDF)',
  'student_payment_history_btn_preparing': 'Preparing...',
  'errors_load_payment_history': 'Failed to load payment history.',
  'errors_download_receipt': 'Could not download receipt.',
  'student_payment_history_verify_success': 'Payment {id} verified successfully.',
  'student_payment_history_verify_pending': 'Payment is still processing (Status: {status}).',
  'errors_verify_payment': 'Failed to verify payment. Please try again or contact support.',
  'student_payment_history_verify_title': 'Click to verify this payment status with the gateway',
  'common_verifying': 'Verifying...',
  'student_payment_history_verify_action': 'Verify Status',

  // Academic Promotion Engine
  'academic_session_title': 'Academic Sessions',
  'academic_session_create_btn': 'Create New Session',
  'academic_session_status_draft': 'DRAFT',
  'academic_session_status_active': 'ACTIVE',
  'academic_session_status_closing': 'CLOSING',
  'academic_session_status_closed': 'CLOSED',
  'academic_session_status_failed': 'FAILED',
  'academic_session_close_confirm': 'Are you sure you want to close this session? This will trigger batch calculations.',
  'academic_session_close_progress': 'Processing promotions: {processed} / {total} students',
  'academic_session_override_justification_req': 'Justification is required to override the promotion result.',
  'academic_record_import_drag': 'Drag and drop your academic record CSV file here, or click to browse',
  'academic_record_import_success': 'Import session {id} successfully completed. {rows} records processed.',
  'academic_record_import_error_row': 'Row {row}: {error}',
  'academic_deferred_force_resolve': 'Force Resolve Student',
  'academic_gate_status_failed': 'Failed',
  'academic_gate_metric_actual': 'Actual',
  'academic_gate_metric_threshold': 'Threshold',

  // Academic Progression Portal
  'academic_readiness_green': 'All students have GPA records for the current semester',
  'academic_readiness_yellow': 'More than 80% of students have GPA records',
  'academic_readiness_red': 'Less than 80% of students have GPA records',
  'academic_readiness_black': 'Critical: Less than 50% GPA coverage or active import errors',
  'academic_promotion_progress_title': 'Promotion Progress Tracker',
  'academic_promotion_progress_processed': 'Processed Students',
  'academic_promotion_progress_remaining': 'Remaining Students',
  'academic_promotion_progress_promoted': 'Promoted',
  'academic_promotion_progress_held_back': 'Held Back',
  'academic_promotion_progress_deferred': 'Deferred',
  'academic_promotion_progress_eta': 'Estimated Time Remaining',
  'academic_gate_failure_detail_title': 'Gate Failure Drill-down',
  'academic_gate_failure_detail_desc': 'Detailed failure analysis for student gates.',
  'academic_results_export_btn': 'Export CSV',
  'academic_results_export_success': 'CSV Export completed successfully.',
  'academic_results_export_error': 'Failed to export CSV.',
  'student_academic_position_title': 'Academic Position',
  'student_academic_year_level': 'Current Year Level',
  'student_academic_semester': 'Current Semester',
  'student_last_promotion_action': 'Last Promotion Action',
  'academic_session_filter_show_archived': 'Show Archived Sessions',
  'dept_semesters_per_year': 'Semesters Per Year',
  'dept_max_year_levels': 'Maximum Year Levels',
  'dept_academic_structure_section': 'Academic Progression Structure',

  // Promotion Exceptions Inspector Labels
  'academic_inspector_search_label': 'Search by Student ID',
  'academic_inspector_gate_label': 'Filter by Gate',
  'academic_inspector_category_label': 'Filter by Category',
  'academic_inspector_empty_title': 'No Exceptions Detected',
  'academic_inspector_empty_desc': 'All processed students have successfully bypassed eligibility gates. No exceptions exist for the current filters.'
};


declare global {
  interface Window {
    __RUNTIME_CONFIG__?: {
      VITE_API_BASE_URL?: string;
      VITE_RECAPTCHA_SITE_KEY?: string;
      VITE_SENTRY_DSN?: string;
      [key: string]: unknown;
    };
  }
}

class ConfigurationService {
  private static instance: ConfigurationService;
  private frontendConfig: FrontendConfiguration | null = null;
  private initialized: boolean = false;
  private labelCache: Map<string, string> = new Map();

  private constructor() {
    // Validate production configuration on initialization
    this.validateProductionConfig();
  }

  public static getInstance(): ConfigurationService {
    if (!ConfigurationService.instance) {
      ConfigurationService.instance = new ConfigurationService();
    }
    return ConfigurationService.instance;
  }

  /**
   * Validates that required configuration is present in production builds.
   * Throws error if critical config is missing to prevent silent failures.
   */
  private validateProductionConfig(): void {
    if (!import.meta.env.PROD) return;

    if (window.__RUNTIME_CONFIG__ && !Object.isFrozen(window.__RUNTIME_CONFIG__)) {
      logger.error('Runtime config tamper detected: window.__RUNTIME_CONFIG__ is not frozen.', { context: 'ConfigurationService' });
    }

    const requiredVars = [
      'VITE_API_BASE_URL',
      'VITE_SENTRY_DSN',
      'VITE_RECAPTCHA_SITE_KEY',
      'VITE_CIRCUIT_FAILURE_THRESHOLD',
      'VITE_CIRCUIT_RESET_TIMEOUT_MS',
      'VITE_OFFLINE_STORAGE_KEY',
    ];

    const missing: string[] = [];
    const invalid: string[] = [];

    requiredVars.forEach((key) => {
      const value = window.__RUNTIME_CONFIG__?.[key] || import.meta.env[key];

      if (!value) {
        missing.push(key);
      } else if (this.isDevValue(value)) {
        invalid.push(`${key}: contains development value (${value})`);
      }
    });

    if (missing.length > 0 || invalid.length > 0) {
      const errors: string[] = [];
      if (missing.length > 0) {
        errors.push(`Missing: ${missing.join(', ')}`);
      }
      if (invalid.length > 0) {
        errors.push(`Invalid: ${invalid.join(', ')}`);
      }
      throw new Error(`Production config validation failed. ${errors.join(' | ')}`);
    }
  }

  /**
   * Checks if a value appears to be a development/test value
   */
  private isDevValue(value: string): boolean {
    const devIndicators = ['localhost', '127.0.0.1', 'test', 'example', '6LeIxAcTA'];
    return devIndicators.some((indicator) => value.includes(indicator));
  }

  /**
   * Retrieves the API Base URL.
   * Priority:
   * 1. Runtime Config (window.__RUNTIME_CONFIG__)
   * 2. Vite Environment Variable (import.meta.env)
   * 3. Fallback (localhost - dev only)
   */
  public getApiBaseUrl(): string {
    const url = window.__RUNTIME_CONFIG__?.VITE_API_BASE_URL || import.meta.env.VITE_API_BASE_URL;

    if (!url) {
      // In production, this should never happen due to validateProductionConfig
      if (import.meta.env.PROD) {
        throw new Error('CRITICAL: VITE_API_BASE_URL not configured for production build');
      }
      // Dev fallback with console warning
      console.warn('⚠️ Using localhost fallback - configure VITE_API_BASE_URL in .env');
      return 'http://localhost:3001/api/v1';
    }
    return url;
  }

  /**
   * Retrieves the Socket URL (derived from API Base URL).
   */
  public getSocketUrl(): string {
    const apiBaseUrl = this.getApiBaseUrl();
    try {
      const url = new URL(apiBaseUrl);
      return url.origin;
    } catch {
      // Fallback for relative URLs or invalid URLs
      return apiBaseUrl.replace('/api/v1', '').replace('/api', '');
    }
  }

  /**
   * Retrieves the reCAPTCHA Site Key.
   */
  public getRecaptchaKey(): string {
    const key =
      window.__RUNTIME_CONFIG__?.VITE_RECAPTCHA_SITE_KEY || import.meta.env.VITE_RECAPTCHA_SITE_KEY;

    if (!key) {
      if (import.meta.env.PROD) {
        throw new Error('CRITICAL: VITE_RECAPTCHA_SITE_KEY not configured');
      }
      console.warn('⚠️ VITE_RECAPTCHA_SITE_KEY not configured - reCAPTCHA will be disabled');
      return '';
    }
    return key;
  }

  /**
   * Checks if the application is running in E2E testing mode.
   * When true, certain features like reCAPTCHA are bypassed for automated testing.
   * 
   * ⚠️ SECURITY NOTE:
   * This is a read-only check that determines if the environment variable
   * VITE_E2E_TESTING is set to 'true'. This variable:
   * 
   * 1. Defaults to 'false' in .env.example (production-safe)
   * 2. Is only set to 'true' by Playwright config during E2E test runs
   * 3. Is a build-time variable that cannot be modified at runtime by users
   * 4. Should NEVER be enabled in production or staging environments
   * 
   * The bypass only affects the login flow's reCAPTCHA check, allowing
   * automated tests to authenticate without Google's reCAPTCHA service.
   * 
   * @returns true if E2E testing mode is enabled, false otherwise (default)
   */
  public isE2ETesting(): boolean {
    const value = window.__RUNTIME_CONFIG__?.VITE_E2E_TESTING || import.meta.env.VITE_E2E_TESTING;
    return value === 'true' || value === true;
  }

  /**
   * Retrieves the Sentry DSN for error tracking.
   * Returns empty string in development to disable Sentry.
   */
  public getSentryDsn(): string {
    const dsn = window.__RUNTIME_CONFIG__?.VITE_SENTRY_DSN || import.meta.env.VITE_SENTRY_DSN;

    if (!dsn) {
      if (import.meta.env.PROD) {
        // In production, return empty string to disable Sentry rather than failing
        // This allows the app to run without error tracking if not configured
        console.warn('⚠️ VITE_SENTRY_DSN not configured - error tracking disabled');
        return '';
      }
      // In development, Sentry is optional
      return '';
    }
    return dsn;
  }

  /**
   * Retrieves the Google reCAPTCHA API script URL.
   * Allows for custom CDN, proxy, or self-hosted reCAPTCHA.
   *
   * Priority:
   * 1. Runtime Config (window.__RUNTIME_CONFIG__)
   * 2. Environment Variable (import.meta.env)
   * 3. Fallback (Google's default CDN)
   *
   * @returns reCAPTCHA API URL
   */
  public getRecaptchaApiUrl(): string {
    const url =
      window.__RUNTIME_CONFIG__?.VITE_RECAPTCHA_API_URL || import.meta.env.VITE_RECAPTCHA_API_URL;

    if (!url) {
      if (import.meta.env.PROD) {
        console.warn('⚠️ VITE_RECAPTCHA_API_URL not configured - using Google default');
      }
      return 'https://www.google.com/recaptcha/api.js';
    }
    return url;
  }

  /**
   * Retrieves the default login role for /login redirect.
   * When users navigate to /login without specifying a role, they are redirected to /login/{role}.
   *
   * Priority:
   * 1. Runtime Config (window.__RUNTIME_CONFIG__)
   * 2. Environment Variable (import.meta.env)
   * 3. Fallback (student)
   *
   * @returns Default role ('student' | 'staff' | 'admin')
   */
  public getDefaultLoginRole(): string {
    const role =
      window.__RUNTIME_CONFIG__?.VITE_DEFAULT_LOGIN_ROLE || import.meta.env.VITE_DEFAULT_LOGIN_ROLE;

    if (!role) {
      if (import.meta.env.PROD) {
        logger.warn('VITE_DEFAULT_LOGIN_ROLE not configured - using "student" default');
      }
      return 'student';
    }

    // Validate role
    const validRoles = ['student', 'staff', 'admin'];
    if (!validRoles.includes(role.toLowerCase())) {
      logger.warn(`Invalid VITE_DEFAULT_LOGIN_ROLE: "${role}" - using "student" default`);
      return 'student';
    }

    return role.toLowerCase();
  }

  /**
   * Retrieves the maximum allowed file size for feedback attachments.
   * Dynamic: defined in .env, falls back to 5MB.
   * 
   * @returns Maximum file size in bytes
   */
  public getMaxFeedbackFileSize(): number {
    return this.getNumber('VITE_MAX_FEEDBACK_FILE_SIZE', 5 * 1024 * 1024);
  }

  /**
   * Retrieves default pagination settings for the application.
   * 
   * Mandate: No hardcoded page sizes.
   * @returns PaginationConfiguration object
   */
  public getPaginationDefaults() {
    return {
      defaultPageSize: this.getNumber('VITE_DEFAULT_PAGE_SIZE', 20),
    };
  }


  /**
   * Retrieves the default Universal SMS Webhook URL using the configured API base URL.
   * This ensures consistency between frontend instructions and backend routing.
   *
   * @returns Full Universal Webhook URL
   */
  public getUniversalSmsWebhookUrl(): string {
    const baseUrl = this.getApiBaseUrl();
    // Remove trailing slash if present to avoid double slashes
    const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    return `${cleanBaseUrl}/sms/webhook`;
  }

  /**
   * Check if Sentry is enabled based on DSN availability
   */
  public isSentryEnabled(): boolean {
    return !!this.getSentryDsn();
  }

  /**
   * Retrieves retry configuration for API calls.
   * All values configurable via environment variables.
   */
  /**
   * Retrieves retry configuration for API calls.
   * 100% Dynamic: Values are sourced from the backend API with environment fallbacks.
   */
  public getRetryConfig() {
    const api = this.getFrontendConfig().api;
    return {
      // Standard preset: Driven by backend API config
      standard: {
        maxRetries: api.maxRetries || this.getNumber('VITE_RETRY_STANDARD_MAX_RETRIES', 3),
        initialDelayMs: this.getNumber('VITE_RETRY_STANDARD_INITIAL_DELAY', 1000),
        maxDelayMs: this.getNumber('VITE_RETRY_STANDARD_MAX_DELAY', 10000),
        backoffMultiplier: this.getNumber('VITE_RETRY_STANDARD_MULTIPLIER', 2),
      },
      // Quick preset for real-time operations
      quick: {
        maxRetries: this.getNumber('VITE_RETRY_QUICK_MAX_RETRIES', 3),
        initialDelayMs: this.getNumber('VITE_RETRY_QUICK_INITIAL_DELAY', 500),
        maxDelayMs: this.getNumber('VITE_RETRY_QUICK_MAX_DELAY', 5000),
        backoffMultiplier: this.getNumber('VITE_RETRY_QUICK_MULTIPLIER', 2),
      },
      // Patient preset for background operations
      patient: {
        maxRetries: this.getNumber('VITE_RETRY_PATIENT_MAX_RETRIES', 5),
        initialDelayMs: this.getNumber('VITE_RETRY_PATIENT_INITIAL_DELAY', 2000),
        maxDelayMs: this.getNumber('VITE_RETRY_PATIENT_MAX_DELAY', 30000),
        backoffMultiplier: this.getNumber('VITE_RETRY_PATIENT_MULTIPLIER', 2),
      },
      // Aggressive preset for critical operations
      aggressive: {
        maxRetries: this.getNumber('VITE_RETRY_AGGRESSIVE_MAX_RETRIES', 7),
        initialDelayMs: this.getNumber('VITE_RETRY_AGGRESSIVE_INITIAL_DELAY', 500),
        maxDelayMs: this.getNumber('VITE_RETRY_AGGRESSIVE_MAX_DELAY', 15000),
        backoffMultiplier: this.getNumber('VITE_RETRY_AGGRESSIVE_MULTIPLIER', 1.5),
      },
      // Global retry settings
      global: {
        defaultDeadlineMs: this.getNumber('VITE_RETRY_DEFAULT_DEADLINE_MS', 60000),
        enableTelemetry: this.get('VITE_RETRY_TELEMETRY_ENABLED') !== 'false',
      },
    };
  }

  /**
   * Retrieves Circuit Breaker configuration.
   * 100% Dynamic: Values are sourced from the backend API with environment fallbacks.
   */
  public getCircuitBreakerConfig() {
    const cb = this.getFrontendConfig().circuitBreaker;
    return {
      threshold: cb?.threshold || this.getNumber('VITE_CIRCUIT_FAILURE_THRESHOLD', 10),
      resetTimeoutMs: cb?.resetTimeoutMs || this.getNumber('VITE_CIRCUIT_RESET_TIMEOUT_MS', 60000),
      pollingIntervalMs: cb?.pollingIntervalMs || this.getNumber('VITE_RESILIENCE_POLLING_INTERVAL_MS', 30000),
      halfOpenTimeoutMs: cb?.halfOpenTimeoutMs || this.getNumber('VITE_CIRCUIT_HALF_OPEN_TIMEOUT_MS', 15000),
    };
  }

  /**
   * Retrieves Request Timeout Configuration.
   * 100% Dynamic: Values are sourced from the backend API with environment fallbacks.
   * @param preset 'default' | 'analytical' | 'FAST_LOOKUP' | 'STANDARD' | 'TRANSACTIONAL'
   */
  public getNetworkTimeout(preset: 'default' | 'analytical' | 'FAST_LOOKUP' | 'STANDARD' | 'TRANSACTIONAL' = 'default'): number {
    const apiConfig = this.frontendConfig?.api;

    if (preset === 'FAST_LOOKUP') {
      return apiConfig?.fastLookupTimeoutMs || this.getNumber('VITE_API_TIMEOUT_FAST_LOOKUP', 15000);
    }
    if (preset === 'TRANSACTIONAL' || preset === 'analytical') {
      return apiConfig?.transactionalTimeoutMs || this.getNumber('VITE_API_TIMEOUT_TRANSACTIONAL', 45000);
    }
    if (preset === 'STANDARD' || preset === 'default') {
      return apiConfig?.standardTimeoutMs || apiConfig?.timeoutMs || this.getNumber('VITE_API_TIMEOUT_MS', 15000);
    }

    return apiConfig?.timeoutMs || this.getNumber('VITE_API_TIMEOUT_MS', 15000);
  }

  /**
   * Retrieves the navigation cache sliding-window TTL in seconds.
   * 100% Dynamic: Sourced from backend API config → env var → static default (120s).
   * Allows runtime tuning of navigation cache freshness without code changes.
   *
   * @returns TTL in seconds
   */
  public getNavigationCacheTtlSeconds(): number {
    const apiConfig = this.frontendConfig?.api;
    const dbValue = (apiConfig as Record<string, unknown> | undefined)?.navigationCacheTtlSeconds;
    if (typeof dbValue === 'number' && dbValue > 0) return dbValue;
    return this.getNumber('VITE_NAV_CACHE_TTL_SECONDS', NAV_CACHE_CONFIG.TTL_SECONDS);
  }

  /**
   * Retrieves the navigation cache hard ceiling in seconds.
   * 100% Dynamic: Sourced from backend API config → env var → centralized config constant.
   * Prevents indefinite sliding-window extension.
   *
   * @returns Max cache age in seconds
   */
  public getNavigationMaxCacheAgeSeconds(): number {
    const apiConfig = this.frontendConfig?.api;
    const dbValue = (apiConfig as Record<string, unknown> | undefined)?.navigationMaxCacheAgeSeconds;
    if (typeof dbValue === 'number' && dbValue > 0) return dbValue;
    return this.getNumber('VITE_NAV_MAX_CACHE_AGE_SECONDS', NAV_CACHE_CONFIG.MAX_CACHE_AGE_SECONDS);
  }

  /**
   * Retrieves the dashboard widget auto-refresh (polling) interval.
   * Dynamic: defined in .env via VITE_DASHBOARD_POLLING_INTERVAL_MS, falls back to 60s.
   *
   * @returns Polling interval in milliseconds
   */
  public getDashboardPollingInterval(): number {
    return this.getNumber('VITE_DASHBOARD_POLLING_INTERVAL_MS', 60000);
  }

  /**
   * Retrieves the stagger delay between dashboard widget bootstrap initializations.
   * Prevents simultaneous mount → simultaneous network burst → 429 rate-limit cascade.
   * Dynamic: defined in .env via VITE_WIDGET_STAGGER_DELAY_MS, falls back to 250ms.
   *
   * @returns Stagger delay in milliseconds per widget slot
   */
  public getWidgetStaggerDelayMs(): number {
    return this.getNumber('VITE_WIDGET_STAGGER_DELAY_MS', 250);
  }

  /**
   * Retrieves the failure budget for batch polling.
   */
  public getBatchPollFailureBudget(): number {
    return this.getNumber('VITE_BATCH_POLL_FAILURE_BUDGET', 3);
  }


  /**
   * Helper to get a numeric configuration value with a default fallback.
   */
  private getNumber(key: string, defaultValue: number): number {
    const value = window.__RUNTIME_CONFIG__?.[key] || import.meta.env[key];
    if (value === undefined || value === null || value === '') {
      return defaultValue;
    }
    const parsed = Number(value);
    return isNaN(parsed) ? defaultValue : parsed;
  }

  /**
   * Generic getter for other config values.
   */
  public get(key: string): unknown {
    return window.__RUNTIME_CONFIG__?.[key] || import.meta.env[key];
  }

  /**
   * WebSocket Event Names Configuration
   * These event names define the protocol contract between frontend and backend.
   * Configurable via environment variables for deployment flexibility.
   */
  public getWsEventNewNotification(): string {
    return (this.get('VITE_WS_EVENT_NEW_NOTIFICATION') as string) || 'new_notification';
  }

  public getWsEventPaymentStatusUpdated(): string {
    return (this.get('VITE_WS_EVENT_PAYMENT_STATUS_UPDATED') as string) || 'payment:status-updated';
  }

  public getWsEventPaymentQueueUpdated(): string {
    return (this.get('VITE_WS_EVENT_PAYMENT_QUEUE_UPDATED') as string) || 'payment:queue-updated';
  }

  public getWsEventScholarshipStatusUpdated(): string {
    return (this.get('VITE_WS_EVENT_SCHOLARSHIP_STATUS_UPDATED') as string) || 'scholarship:status-updated';
  }

  public getWsEventSecurityAnomalyDetected(): string {
    return (this.get('VITE_WS_EVENT_SECURITY_ANOMALY_DETECTED') as string) || 'security:anomaly-detected';
  }

  public getWsEventSecurityMitigationUpdated(): string {
    return (this.get('VITE_WS_EVENT_SECURITY_MITIGATION_UPDATED') as string) || 'security:mitigation-updated';
  }

  public getWsEventRolePermissionsUpdated(): string {
    return (this.get('VITE_WS_EVENT_ROLE_PERMISSIONS_UPDATED') as string) || 'role:permissions-updated';
  }

  /**
   * Retrieves a dynamic UI label from the central Label Registry with support for interpolation.
   *
   * PERFORMANCE:
   * - O(1) sub-millisecond lookups via internal memoization Map for templates.
   * - Prevents repeated object traversal and property access.
   *
   * SECURITY:
   * - Fallback logic ensures UI reliability even if labels are missing.
   * - Variable interpolation is safe and deterministic.
   *
   * @param key The unique key for the UI label
   * @param defaultValueOrParams Optional fallback value OR variables for interpolation
   * @param params Optional variables for interpolation if a defaultValue was provided
   * @returns The resolved and interpolated label string
   */
  public getLabel(key: string, defaultValueOrParams?: string | LabelParams, params?: LabelParams): string {
    let defaultValue: string | undefined;
    let actualParams: LabelParams | undefined;

    if (typeof defaultValueOrParams === 'string') {
      defaultValue = defaultValueOrParams;
      actualParams = params;
    } else {
      actualParams = defaultValueOrParams;
    }

    // 1. Resolve the template (O(1) with memoization)
    let template = this.labelCache.get(key);
    if (template === undefined) {
      const remoteLabels = this.frontendConfig?.labels || {};
      template = remoteLabels[key] || defaultValue || DEFAULT_LABELS[key] || key;
      this.labelCache.set(key, template);
    }

    // 2. Perform interpolation if parameters are provided
    if (actualParams && Object.keys(actualParams).length > 0) {
      return this.interpolate(template, actualParams);
    }

    return template;
  }

  /**
   * Internal helper for string interpolation using {key} syntax.
   */
  private interpolate(template: string, params: LabelParams): string {
    try {
      return template.replace(/{(\w+)}/g, (match, key) => {
        // SECURITY: Sanitize all dynamic parameters to prevent XSS/HTML Injection
        // Follows the "NEVER TRUST THE FRONTEND" principle by escaping potential payloads.
        const value = params[key];
        if (value === undefined || value === null) return match;

        const stringValue = String(value);
        return sanitizeText(stringValue);
      });
    } catch (error) {
      logger.warn(`Interpolation failed for template: ${template}`, { error, params });
      return template;
    }
  }

  /**
   * Initializes the configuration service by fetching the authoritative configuration
   * from the backend API. This method MUST be called once during application bootstrap,
   * before any component reads theme, label, or API config values.
   *
   * STRATEGY (Cache as Disaster-Recovery, not Primary Path):
   * We intentionally ALWAYS await a live network fetch on startup. This is the only
   * way to guarantee that ThemeProvider receives the most up-to-date theme on every
   * page load — eliminating the stale-cache race condition where a cached theme is
   * injected into the DOM before the background refresh completes.
   *
   * The localStorage cache is retained ONLY as a resilience fallback for when the
   * network is unavailable (e.g., offline mode, backend temporarily down). In that
   * case, the user gets the last-known-good config rather than bare fallback defaults.
   *
   * @returns Promise<void> — resolves once configuration is fully loaded and normalized.
   */
  public async initialize(): Promise<void> {
    if (this.initialized) {
      logger.warn('ConfigurationService already initialized — skipping duplicate call.');
      return;
    }

    logger.info('Initializing ConfigurationService — fetching authoritative config from backend...');

    // Pre-load cache as a disaster-recovery safety net.
    // It is NOT used as the primary source — only if the network fetch fails below.
    const disasterRecoveryCache = this.shouldUseCache() ? this.getFromCache() : null;

    try {
      // Always fetch fresh configuration from the backend. This is the authoritative
      // source of truth and is the only guarantee that ThemeProvider gets current theme
      // data before it mounts and injects CSS variables into the DOM.
      const ConfigurationApi = (await import('./api/configurationApi')).default;
      const remoteConfig = await ConfigurationApi.getFrontendConfig();
      this.frontendConfig = this.normalizeConfig(remoteConfig);
      this.initialized = true;

      // Persist the fresh config to localStorage for the next cold-start disaster recovery.
      if (this.shouldUseCache()) {
        this.saveToCache(this.frontendConfig);
      }

      logger.success('ConfigurationService initialized successfully from backend.');
    } catch (error) {
      // Network fetch failed. Prioritize the last-known-good cache over bare defaults
      // to preserve the user's custom theme and settings even while offline.
      if (disasterRecoveryCache) {
        logger.warn(
          'Backend fetch failed during initialization. Falling back to disaster-recovery cache.',
          { error: error instanceof Error ? error.message : String(error) }
        );
        this.frontendConfig = disasterRecoveryCache;
      } else {
        // No cache available — use hardcoded fallback defaults as the last resort.
        logger.error(
          'Backend fetch failed and no cache is available. Applying hardcoded fallback defaults.',
          error,
          'ConfigurationService.initialize'
        );
        this.frontendConfig = this.getFallbackConfig();
      }
      this.initialized = true;
    }
  }


  /**
   * Normalizes configuration by merging with defaults.
   * Ensures structural integrity for nested objects.
   */
  private normalizeConfig(config: FrontendConfiguration): FrontendConfiguration {
    const defaults = this.getFallbackConfig();

    return {
      ...config,
      api: {
        ...defaults.api,
        ...config.api,
        toastDurationInstitutional: config.api?.toastDurationInstitutional || defaults.api.toastDurationInstitutional
      },
      pagination: { ...defaults.pagination, ...config.pagination },
      security: {
        ...defaults.security,
        ...(config.security || {}),
        pbkdf2Iterations: config.security?.pbkdf2Iterations || defaults.security.pbkdf2Iterations,
        aesKeyLengthBits: config.security?.aesKeyLengthBits || defaults.security.aesKeyLengthBits
      },
      labels: { ...DEFAULT_LABELS, ...(config.labels || {}) },
      theme: {
        ...defaults.theme,
        ...config.theme,
        tokenMappings: {
          ...defaults.theme.tokenMappings,
          ...(config.theme?.tokenMappings || {}),
          padding: { ...defaults.theme.tokenMappings.padding, ...(config.theme?.tokenMappings?.padding || {}) },
          fontSize: { ...defaults.theme.tokenMappings.fontSize, ...(config.theme?.tokenMappings?.fontSize || {}) },
          fontWeight: { ...defaults.theme.tokenMappings.fontWeight, ...(config.theme?.tokenMappings?.fontWeight || {}) },
          letterSpacing: { ...defaults.theme.tokenMappings.letterSpacing, ...(config.theme?.tokenMappings?.letterSpacing || {}) },
          statusClasses: { ...defaults.theme.tokenMappings.statusClasses, ...(config.theme?.tokenMappings?.statusClasses || {}) },
        },
        typography: (() => {
          const remoteTypo = config.theme?.typography || {};
          const remoteFontSize = (remoteTypo as Record<string, unknown>)?.fontSize as Record<string, unknown> | undefined;
          const defaultFontSize = defaults.theme.typography.fontSize;
          // Cast all fontSize values to strings — backend may store as numbers
          const normalizedFontSize = {
            ...defaultFontSize,
            ...(remoteFontSize ? Object.fromEntries(
              Object.entries(remoteFontSize).map(([k, v]) => [k, String(v)])
            ) : {})
          };
          // Cast lineHeight to string if numeric
          const rawLineHeight = (remoteTypo as Record<string, unknown>)?.lineHeight;
          const normalizedLineHeight = rawLineHeight !== undefined
            ? String(rawLineHeight)
            : defaults.theme.typography.lineHeight;
          return {
            ...defaults.theme.typography,
            ...(remoteTypo as object),
            fontSize: normalizedFontSize,
            lineHeight: normalizedLineHeight,
            tracking: {
              ...defaults.theme.typography.tracking,
              ...((remoteTypo as Record<string, unknown>)?.tracking as object || {})
            },
            fontFeatureSettings: (remoteTypo as Record<string, unknown>)?.fontFeatureSettings as string || defaults.theme.typography.fontFeatureSettings
          };
        })(),
        statusColors: {
          ...defaults.theme.statusColors,
          ...(config.theme?.statusColors || {})
        },
        aesthetics: {
          ...defaults.theme.aesthetics,
          ...(config.theme?.aesthetics || {}),
          glassmorphism: {
            ...defaults.theme.aesthetics.glassmorphism,
            ...(config.theme?.aesthetics?.glassmorphism || {})
          },
          motion: {
            ...defaults.theme.aesthetics.motion,
            ...(config.theme?.aesthetics?.motion || {})
          },
          depth: {
            ...defaults.theme.aesthetics.depth,
            ...(config.theme?.aesthetics?.depth || {})
          },
          layout: {
            ...defaults.theme.aesthetics.layout,
            ...(config.theme?.aesthetics?.layout || {})
          }
        },
        table: {
          ...defaults.theme.table,
          ...(config.theme?.table || {}),
          stripeStyle: config.theme?.table?.stripeStyle || defaults.theme.table.stripeStyle,
          borderStyle: config.theme?.table?.borderStyle || defaults.theme.table.borderStyle,
          headerVariant: config.theme?.table?.headerVariant || defaults.theme.table.headerVariant,
          rowSpacing: config.theme?.table?.rowSpacing || defaults.theme.table.rowSpacing,
          containerStyle: config.theme?.table?.containerStyle || defaults.theme.table.containerStyle,
          tableHeaderWrap: config.theme?.table?.tableHeaderWrap || defaults.theme.table.tableHeaderWrap,
          tableCellWrap: config.theme?.table?.tableCellWrap || defaults.theme.table.tableCellWrap,
          stickyShadow: config.theme?.table?.stickyShadow || defaults.theme.table.stickyShadow,
          collapsibleActions: config.theme?.table?.collapsibleActions ?? defaults.theme.table.collapsibleActions,
          tableRowContrast: config.theme?.table?.tableRowContrast ?? defaults.theme.table.tableRowContrast,
          tableRowHoverContrast: config.theme?.table?.tableRowHoverContrast ?? defaults.theme.table.tableRowHoverContrast,
          tableTextSize: config.theme?.table?.tableTextSize || defaults.theme.table.tableTextSize
        },
        // Deep-merge each mode using the dedicated helper to prevent structural corruption
        // from shallow spreads when the backend returns an incomplete JSONB blob.
        darkMode: this.deepMergeModeColors(defaults.theme.darkMode, config.theme?.darkMode),
        lightMode: this.deepMergeModeColors(defaults.theme.lightMode, config.theme?.lightMode),
      }
    };
  }

  /**
   * Performs a structural deep-merge of a ThemeModeColors object from the backend
   * on top of the complete fallback defaults.
   *
   * WHY THIS EXISTS:
   * The backend stores darkMode/lightMode as JSONB blobs in PostgreSQL. When Prisma
   * serialises and returns these blobs, nested optional sub-objects (elevation,
   * statusColors, aesthetics, table, tokenMappings) may be partially present or
   * entirely absent — especially for older records created before new fields were added.
   * A simple spread (`...remote`) overwrites the fallback's complete nested structure
   * with the backend's incomplete version, resulting in `undefined` values that cascade
   * into broken CSS variable injection inside ThemeProvider.
   *
   * This method ensures every nested sub-object is independently merged, so the result
   * always satisfies the full ThemeModeColors interface contract.
   *
   * @param fallback - The complete, type-safe fallback ThemeModeColors (from getFallbackConfig)
   * @param remote   - The partial ThemeModeColors from the backend API (may be incomplete)
   * @returns A fully merged ThemeModeColors guaranteed to be structurally complete
   */
  private deepMergeModeColors(
    fallback: ThemeModeColors,
    remote: Partial<ThemeModeColors> | undefined | null
  ): ThemeModeColors {
    // If the backend returned nothing for this mode, use the fallback as-is.
    if (!remote) {
      return { ...fallback };
    }

    return {
      // Spread all flat scalar fields — backend values take precedence.
      ...fallback,
      ...remote,

      // Deep-merge the nested `elevation` object independently.
      // This prevents a partial backend elevation object from stripping rimLightOpacity/shadowColor.
      elevation: {
        ...fallback.elevation,
        ...(remote.elevation ?? {}),
      },

      // Merge optional mode-specific status color overrides.
      // If the backend omits this block entirely, the global-level statusColors take effect.
      statusColors: remote.statusColors
        ? { ...fallback.statusColors, ...remote.statusColors }
        : fallback.statusColors,

      // Deep-merge the optional mode-specific aesthetics override block.
      // Each sub-key (glassmorphism, motion, depth, layout) is merged independently
      // to preserve any fields the backend did not include.
      aesthetics: remote.aesthetics
        ? {
            ...fallback.aesthetics,
            ...remote.aesthetics,
            glassmorphism: {
              ...(fallback.aesthetics?.glassmorphism ?? {}),
              ...(remote.aesthetics.glassmorphism ?? {}),
            },
            motion: {
              ...(fallback.aesthetics?.motion ?? {}),
              ...(remote.aesthetics.motion ?? {}),
            },
            depth: {
              ...(fallback.aesthetics?.depth ?? {}),
              ...(remote.aesthetics.depth ?? {}),
            },
            layout: {
              ...(fallback.aesthetics?.layout ?? {}),
              ...(remote.aesthetics.layout ?? {}),
            },
          }
        : fallback.aesthetics,

      // Merge the optional mode-specific table configuration override.
      table: remote.table
        ? { ...fallback.table, ...remote.table }
        : fallback.table,

      // Deep-merge the optional mode-specific token mappings, one sub-key at a time.
      // Record<string, string> sub-maps are individually spread to avoid silent key loss.
      tokenMappings: remote.tokenMappings
        ? {
            ...(fallback.tokenMappings ?? {}),
            ...remote.tokenMappings,
            padding: {
              ...(fallback.tokenMappings?.padding ?? {}),
              ...(remote.tokenMappings.padding ?? {}),
            },
            fontSize: {
              ...(fallback.tokenMappings?.fontSize ?? {}),
              ...(remote.tokenMappings.fontSize ?? {}),
            },
            fontWeight: {
              ...(fallback.tokenMappings?.fontWeight ?? {}),
              ...(remote.tokenMappings.fontWeight ?? {}),
            },
            letterSpacing: {
              ...(fallback.tokenMappings?.letterSpacing ?? {}),
              ...(remote.tokenMappings.letterSpacing ?? {}),
            },
            statusClasses: {
              ...(fallback.tokenMappings?.statusClasses ?? {}),
              ...(remote.tokenMappings.statusClasses ?? {}),
            },
          }
        : fallback.tokenMappings,
    };
  }

  /**
   * Checks if configuration caching is enabled via environment variables.
   */
  private shouldUseCache(): boolean {
    const enabled = this.get(PERFORMANCE_ENV_KEYS.ENABLE_CONFIG_CACHING);
    // Default to true if not specified
    return enabled !== 'false' && enabled !== false;
  }

  /**
   * Gets the configured cache key.
   * Dynamic: defined in .env, falls back to default.
   */
  private getCacheKey(): string {
    return (this.get(PERFORMANCE_ENV_KEYS.CONFIG_CACHE_KEY) as string) || PERFORMANCE_DEFAULTS.CONFIG_CACHE_KEY;
  }

  /**
   * Gets the configured cache version.
   * Dynamic: defined in .env, falls back to default.
   */
  private getCacheVersion(): number {
    return this.getNumber(PERFORMANCE_ENV_KEYS.CONFIG_CACHE_VERSION, PERFORMANCE_DEFAULTS.CONFIG_CACHE_VERSION);
  }

  /**
   * Retrieves valid configuration from localStorage.
   * Returns null if cache is missing, invalid, or expired.
   */
  private getFromCache(): FrontendConfiguration | null {
    try {
      const key = this.getCacheKey();
      const raw = localStorage.getItem(key);
      if (!raw) return null;

      const entry: CacheEntry<FrontendConfiguration> = JSON.parse(raw); // Parse first

      // Check Version mismatch
      const currentVersion = this.getCacheVersion();
      if (entry.version !== currentVersion) {
        logger.debug(`Configuration cache version mismatch (got ${entry.version}, expected ${currentVersion})`);
        localStorage.removeItem(key);
        return null;
      }

      // Check TTL
      const ttl = this.getNumber(PERFORMANCE_ENV_KEYS.CONFIG_CACHE_TTL_MS, PERFORMANCE_DEFAULTS.CONFIG_CACHE_TTL_MS);
      if (ttl > 0 && Date.now() - entry.timestamp > ttl) {
        logger.debug('Configuration cache expired');
        localStorage.removeItem(key);
        return null;
      }

      // Check structural integrity (New: Typography block)
      if (!entry.data?.theme?.typography) {
        logger.warn('Configuration cache missing typography structure, invalidating...');
        localStorage.removeItem(key);
        return null;
      }

      return entry.data;
    } catch (error) {
      logger.warn('Failed to parse cached configuration', { error: error instanceof Error ? error.message : String(error) });
      // Clean up potentially corrupted key
      localStorage.removeItem(this.getCacheKey());
      return null;
    }
  }

  /**
   * Saves configuration to localStorage with metadata.
   * SECURITY: Only caches non-sensitive frontend config (theme, API settings).
   * DSNs, keys, and secrets are NOT part of FrontendConfiguration type.
   */
  private saveToCache(config: FrontendConfiguration): void {
    try {
      const entry: CacheEntry<FrontendConfiguration> = {
        data: config,
        timestamp: Date.now(),
        version: this.getCacheVersion()
      };
      localStorage.setItem(this.getCacheKey(), JSON.stringify(entry));
    } catch (error) {
      logger.warn('Failed to save configuration to cache', { error: error instanceof Error ? error.message : String(error) });
    }
  }

  /**
   * Forces a reload of the configuration from the backend.
   * Useful when configuration is updated via the Admin UI.
   */
  public async reload(): Promise<void> {
    // SECURITY/INTEGRITY: Invalidate cache before reload to ensure fresh fetch
    if (this.shouldUseCache()) {
      localStorage.removeItem(this.getCacheKey());
    }

    this.labelCache.clear();
    this.initialized = false;
    await this.initialize();

    // Dispatch custom event to notify components (like ThemeContext) that config has been reloaded
    window.dispatchEvent(new CustomEvent(CONFIG_EVENTS.UPDATED, {
      detail: { key: UPDATE_KEYS.ALL }
    }));
  }

  /**
   * Fallback configuration when backend is unavailable.
   * Contains safe defaults for all configuration values.
   */
  public getFallbackConfig(): FrontendConfiguration {
    return {
      api: {
        timeoutMs: this.getNumber('VITE_API_TIMEOUT_MS', 45000),
        maxRetries: this.getNumber('VITE_API_MAX_RETRIES', 3),
        dateFormat: (this.get('VITE_API_DATE_FORMAT') as string) || 'yyyy/MM/dd',
        cacheMaxAge: this.getNumber('VITE_API_CACHE_MAX_AGE', 300),
        toastDurationInstitutional: this.getNumber('VITE_TOAST_DURATION_INSTITUTIONAL', 20000),
        csrf: {
          cookieName: 'csrf-token',
          headerName: 'X-CSRF-Token'
        },
        fastLookupTimeoutMs: this.getNumber('VITE_API_TIMEOUT_FAST_LOOKUP', 3000),
        standardTimeoutMs: this.getNumber('VITE_API_TIMEOUT_MS', 15000),
        transactionalTimeoutMs: this.getNumber('VITE_API_TIMEOUT_TRANSACTIONAL', 45000),
      },
      labels: DEFAULT_LABELS,
      circuitBreaker: {
        threshold: this.getNumber('VITE_CIRCUIT_FAILURE_THRESHOLD', 10),
        resetTimeoutMs: this.getNumber('VITE_CIRCUIT_RESET_TIMEOUT_MS', 60000),
        pollingIntervalMs: this.getNumber('VITE_RESILIENCE_POLLING_INTERVAL_MS', 30000),
        halfOpenTimeoutMs: this.getNumber('VITE_CIRCUIT_HALF_OPEN_TIMEOUT_MS', 15000),
      },
      security: {
        sensitivePatterns: this.getSecuritySensitivePatterns(),
        maxBroadcastAgeMs: this.getNumber('VITE_MAX_SESSION_BROADCAST_AGE_MS', 5000),
        pbkdf2Iterations: this.getNumber('VITE_CRYPTO_PBKDF2_ITERATIONS', 100000),
        aesKeyLengthBits: this.getNumber('VITE_CRYPTO_AES_KEY_LENGTH_BITS', 256)
      },
      pagination: {
        defaultPageSize: 20
      },
      theme: {
        primaryColor: '#3b82f6',
        secondaryColor: '#10b981',
        accentColor: '#f59e0b',
        darkMode: {
          bgPrimary: '#1f2937',
          bgSecondary: '#111827',
          bgSurface: '#111827',
          textPrimary: '#f9fafb',
          textSecondary: '#d1d5db',
          sidebarText: '#ffffff',
          sidebarIcon: '#ffffff',
          divider: '#374151',
          partition: '#1f2937',
          chartPalette: ['#0ea5e9', '#14b8a6', '#f43f5e', '#16a34a', '#eab308', '#a855f7', '#6366f1'],
          chartGrid: 'rgba(255, 255, 255, 0.05)',
          statsContainerBg: '#111827',
          statsContainerOpacity: 1,
          dashboardCardBg: '#1f2937',
          dashboardCardOpacity: 1,
          selection: '#3b82f6',
          hoverOverlay: 'rgba(255, 255, 255, 0.05)',
          successSurface: 'rgba(34, 197, 94, 0.1)',
          successAccent: '#4ade80',
          warningSurface: 'rgba(234, 179, 8, 0.1)',
          warningAccent: '#fde047',
          errorSurface: 'rgba(239, 68, 68, 0.1)',
          errorAccent: '#f87171',
          infoSurface: 'rgba(59, 130, 246, 0.1)',
          infoAccent: '#60a5fa',
          cardBg: '#1f2937',
          tableBg: '#1f2937',
          tableBgOpacity: 1.0,
          tableHeaderBg: '#111827',
          tableHeaderBgOpacity: 1.0,
          tableHeaderColor: '#f9fafb',
          headerText: '#f9fafb',
          tableBorder: '#374151',
          tableBorderOpacity: 1.0,
          tableStripeColor1: '#f8fafc',
          tableStripeOpacity1: 0.04,
          tableStripeColor2: '#f1f5f9',
          tableStripeOpacity2: 0.035,
          tableHoverColor: '#f9fafb',
          tableHoverOpacity: 0.08,
          tableSelectedColor: '#3b82f6',
          tableSelectedOpacity: 0.15,
          elevation: {
            rimLightOpacity: 0.1,
            shadowColor: 'rgba(0,0,0,0.5)'
          },
          chartTextColor: '#f9fafb',
          chartFontSize: 12,
          chartFontFamily: 'Inter',
          chartFontWeight: '600',
          tokenMappings: {
            padding: { 'px-2': '0.5rem', 'px-4': '1rem', 'px-6': '1.5rem', 'py-2': '0.5rem', 'py-4': '1rem', 'py-6': '1.5rem' },
            fontSize: { 'text-xs': '0.75rem', 'text-sm': '0.875rem', 'text-base': '1rem' },
            fontWeight: { 'font-normal': '400', 'font-medium': '500', 'font-semibold': '600', 'font-bold': '700' },
            letterSpacing: { 'tracking-normal': '0em', 'tracking-wide': '0.025em', 'tracking-wider': '0.05em' },
            statusClasses: { 'success': 'status-badge-success', 'warning': 'status-badge-warning', 'error': 'status-badge-error', 'info': 'status-badge-info', 'neutral': 'status-badge-neutral' }
          }
        },
        lightMode: {
          bgPrimary: '#ffffff',
          bgSecondary: '#f3f4f6',
          bgSurface: '#ffffff',
          textPrimary: '#111827',
          textSecondary: '#6b7280',
          sidebarText: '#1f2937',
          sidebarIcon: '#4b5563',
          divider: '#e5e7eb',
          partition: '#f9fafb',
          chartPalette: ['#0ea5e9', '#14b8a6', '#f43f5e', '#16a34a', '#eab308', '#a855f7', '#6366f1'],
          chartGrid: 'rgba(0, 0, 0, 0.05)',
          statsContainerBg: '#f8fafc',
          statsContainerOpacity: 1,
          dashboardCardBg: '#ffffff',
          dashboardCardOpacity: 1,
          selection: '#3b82f6',
          hoverOverlay: 'rgba(0, 0, 0, 0.02)',
          successSurface: 'rgba(34, 197, 94, 0.1)',
          successAccent: '#22c55e',
          warningSurface: 'rgba(234, 179, 8, 0.1)',
          warningAccent: '#eab308',
          errorSurface: 'rgba(239, 68, 68, 0.1)',
          errorAccent: '#ef4444',
          infoSurface: 'rgba(59, 130, 246, 0.1)',
          infoAccent: '#3b82f6',
          cardBg: '#ffffff',
          tableBg: '#ffffff',
          tableBgOpacity: 1.0,
          tableHeaderBg: '#f9fafb',
          tableHeaderBgOpacity: 1.0,
          tableHeaderColor: '#111827',
          headerText: '#111827',
          tableBorder: '#e5e7eb',
          tableBorderOpacity: 1.0,
          tableStripeColor1: '#f1f5f9',
          tableStripeOpacity1: 0.04,
          tableStripeColor2: '#e2e8f0',
          tableStripeOpacity2: 0.035,
          tableHoverColor: '#111827',
          tableHoverOpacity: 0.08,
          tableSelectedColor: '#3b82f6',
          tableSelectedOpacity: 0.1,
          elevation: {
            rimLightOpacity: 0.05,
            shadowColor: 'rgba(0,0,0,0.1)'
          },
          chartTextColor: '#111827',
          chartFontSize: 12,
          chartFontFamily: 'Inter',
          chartFontWeight: '600',
          tokenMappings: {
            padding: { 'px-2': '0.5rem', 'px-4': '1rem', 'px-6': '1.5rem', 'py-2': '0.5rem', 'py-4': '1rem', 'py-6': '1.5rem' },
            fontSize: { 'text-xs': '0.75rem', 'text-sm': '0.875rem', 'text-base': '1rem' },
            fontWeight: { 'font-normal': '400', 'font-medium': '500', 'font-semibold': '600', 'font-bold': '700' },
            letterSpacing: { 'tracking-normal': '0em', 'tracking-wide': '0.025em', 'tracking-wider': '0.05em' },
            statusClasses: { 'success': 'status-badge-success', 'warning': 'status-badge-warning', 'error': 'status-badge-error', 'info': 'status-badge-info', 'neutral': 'status-badge-neutral' }
          }
        },
        borderRadius: '0.375rem',
        typography: {
          fontFamily: 'Inter',
          fontSize: {
            base: '16',
            sm: '14',
            lg: '18',
          },
          lineHeight: '1.5',
          tracking: {
            tight: '-0.02em',
            normal: '0em',
            wide: '0.05em',
          },
          fontFeatureSettings: "'cv02', 'cv03', 'tabular-nums'",
        },
        statusColors: {
          success: '#22c55e',
          warning: '#eab308',
          error: '#ef4444',
          neutral: '#94a3b8',
          info: '#3b82f6',
        },
        aesthetics: {
          glassmorphism: {
            enabled: false,
            blurIntensity: '8px',
            transparency: 0.95,
            noiseAlpha: 0.03,
            saturation: 1.2,
          },
          motion: {
            speed: 'standard',
            easingCurve: 'productive',
            staggerDelayMs: 30,
            interactiveScale: true,
            pageTransitionEnabled: true,
            pageTransitionType: 'fade-slide'
          },
          depth: {
            shadowProfile: 'soft',
            shadowColor: 'rgba(0,0,0,0.1)'
          },
          layout: {
            uiDensity: 'default',
            contentMaxWidth: '1440px'
          }
        },
        table: {
          headerPadding: '1rem 1.5rem',
          cellPadding: '1rem 1.5rem',
          headerFontSize: '0.75rem',
          headerFontWeight: '600',
          headerUppercase: true,
          headerLetterSpacing: '0.05em',
          cellVerticalAlign: 'middle',
          stripeStyle: 'subtle',
          borderStyle: 'horizontal',
          headerVariant: 'filled',
          rowSpacing: 'none',
          containerStyle: 'card',
          tableHeaderWrap: 'nowrap',
          tableCellWrap: 'wrap',
          stickyShadow: 'none',
          collapsibleActions: true,
          tableRowContrast: false,
          tableRowHoverContrast: false,
          tableTextSize: 'MD'
        },
        tokenMappings: {
          padding: { 'px-2': '0.5rem', 'px-4': '1rem', 'px-6': '1.5rem', 'py-2': '0.5rem', 'py-4': '1rem', 'py-6': '1.5rem' },
          fontSize: { 'text-xs': '0.75rem', 'text-sm': '0.875rem', 'text-base': '1rem' },
          fontWeight: { 'font-normal': '400', 'font-medium': '500', 'font-semibold': '600', 'font-bold': '700' },
          letterSpacing: { 'tracking-normal': '0em', 'tracking-wide': '0.025em', 'tracking-wider': '0.05em' },
          statusClasses: { 'success': 'status-badge-success', 'warning': 'status-badge-warning', 'error': 'status-badge-error', 'info': 'status-badge-info', 'neutral': 'status-badge-neutral' }
        }
      },
      versions: {
        api: 1,
        security: 1,
        theme: 1,
        labels: 1,
      },
    };
  }

  /**
   * Check if the application is running in production mode.
   * Based on Vite's environment variables.
   */
  public isProduction(): boolean {
    return import.meta.env.PROD;
  }

  /**
   * Returns the complete frontend configuration.
   * Throws error if not initialized in production.
   */
  public getFrontendConfig(): FrontendConfiguration {
    if (!this.initialized) {
      if (import.meta.env.PROD) {
        throw new Error('ConfigurationService not initialized. Call initialize() first.');
      }
      console.warn('⚠️  ConfigurationService not initialized. Returning fallback config.');
      return this.getFallbackConfig();
    }
    return this.frontendConfig!;
  }

  /**
   * Gets API timeout in milliseconds.
   * Dynamic value from backend configuration.
   */
  public getApiTimeoutMs(): number {
    return this.getFrontendConfig().api.timeoutMs;
  }

  /**
   * Gets maximum number of API retries.
   * Dynamic value from backend configuration.
   */
  public getApiMaxRetries(): number {
    return this.getFrontendConfig().api.maxRetries;
  }

  /**
   * Gets date format string for frontend.
   * Dynamic value from backend configuration.
   */
  public getDateFormat(): string {
    return this.getFrontendConfig().api.dateFormat;
  }
  /* Removed duplicate getCircuitBreakerConfig and getRetryConfig implementations */

  /**
   * Gets API cache max age in seconds.
   * Dynamic value from backend configuration.
   */
  public getCacheMaxAge(): number {
    return this.getFrontendConfig().api.cacheMaxAge;
  }

  /**
   * Gets the dynamic duration for institutional subtitle toasts.
   * Established as the single source of truth for all admin info banners.
   */
  public getToastDurationInstitutional(): number {
    return this.getFrontendConfig().api.toastDurationInstitutional;
  }

  /**
   * Returns theme configuration for dynamic theming.
   */
  public getThemeConfig() {
    return this.getFrontendConfig().theme;
  }

  /**
   * Returns elite aesthetics configuration for high-fidelity UI handles.
   */
  public getAestheticsConfig() {
    return this.getThemeConfig().aesthetics;
  }

  /**
   * Validation Limits
   */
  public getValidationLimits() {
    return {
      timeout: {
        min: this.getNumber('VITE_CONFIG_TIMEOUT_MIN_MS', 1000),
        max: this.getNumber('VITE_CONFIG_TIMEOUT_MAX_MS', 60000),
      },
      retries: {
        min: this.getNumber('VITE_CONFIG_RETRIES_MIN', 0),
        max: this.getNumber('VITE_CONFIG_RETRIES_MAX', 10),
      },
      cacheAge: {
        min: this.getNumber('VITE_CONFIG_CACHE_AGE_MIN_SECONDS', 0),
      },
      toastDuration: {
        min: this.getNumber('VITE_TOAST_DURATION_MIN_MS', 1000),
        max: this.getNumber('VITE_TOAST_DURATION_MAX_MS', 60000),
      },
    };
  }

  /**
   * Navigation Configuration
   * Dynamic validation limits for navigation management.
   */
  public getNavigationConfig() {
    return {
      nameMinLength: this.getNumber('VITE_NAVIGATION_NAME_MIN_LENGTH', 1),
      nameMaxLength: this.getNumber('VITE_NAVIGATION_NAME_MAX_LENGTH', 100),
      pathMaxLength: this.getNumber('VITE_NAVIGATION_PATH_MAX_LENGTH', 255),
      iconMaxLength: this.getNumber('VITE_NAVIGATION_ICON_MAX_LENGTH', 100),
      orderMinValue: this.getNumber('VITE_NAVIGATION_ORDER_MIN_VALUE', 0),
      orderMaxValue: this.getNumber('VITE_NAVIGATION_ORDER_MAX_VALUE', 10000),
      permissionMaxLength: this.getNumber('VITE_NAVIGATION_PERMISSION_MAX_LENGTH', 100),
      featureFlagMaxLength: this.getNumber('VITE_NAVIGATION_FEATURE_FLAG_MAX_LENGTH', 100),
    };
  }

  /**
   * Pagination Configuration
   * Dynamic settings for data tables and lists.
   */
  public getPaginationConfig() {
    // Parse size options from comma-separated string
    const optionsStr = (this.get('VITE_PAGINATION_SIZE_OPTIONS') as string) || '10,20,50,100';
    let options: number[] = [10, 20, 50, 100];

    try {
      options = optionsStr.split(',')
        .map((s: string) => parseInt(s.trim(), 10))
        .filter((n: number) => !isNaN(n) && n > 0)
        .sort((a: number, b: number) => a - b);

      if (options.length === 0) options = [10, 20, 50, 100];
    } catch {
      console.warn('Invalid VITE_PAGINATION_SIZE_OPTIONS, using defaults');
    }

    return {
      defaultPageSize: this.getNumber('VITE_PAGINATION_DEFAULT_SIZE', 20),
      maxPageSize: this.getNumber('VITE_PAGINATION_MAX_SIZE', 100),
      maxVisiblePages: this.getNumber('VITE_PAGINATION_MAX_VISIBLE_PAGES', 5),
      pageSizeOptions: options,
    };
  }

  // ============================================================================
  // Internationalization (i18n) Configuration
  // ============================================================================

  /**
   * Gets the default language/locale for the application.
   * Used by i18n initialization and formatters.
   */
  public getDefaultLanguage(): string {
    return (this.get('VITE_I18N_DEFAULT_LANGUAGE') as string) || 'en';
  }

  /**
   * Gets the fallback language when translation is missing.
   */
  public getFallbackLanguage(): string {
    return (this.get('VITE_I18N_FALLBACK_LANGUAGE') as string) || 'en';
  }

  /**
   * Gets the default currency code (ISO 4217).
   * Used by currency formatters.
   */
  public getDefaultCurrency(): string {
    return (this.get('VITE_I18N_DEFAULT_CURRENCY') as string) || 'ETB';
  }

  /**
   * Gets the list of supported languages.
   * Returns array of language codes.
   */
  public getSupportedLanguages(): string[] {
    const value = this.get('VITE_I18N_SUPPORTED_LANGUAGES') as string;
    if (!value) return ['en', 'am', 'om', 'so', 'ti'];

    try {
      const languages = value
        .split(',')
        .map((l: string) => l.trim())
        .filter(Boolean);
      return languages.length > 0 ? languages : ['en'];
    } catch {
      return ['en'];
    }
  }

  /**
   * Gets the locale map for BCP 47 locale codes.
   * Format: { lang: 'locale' } e.g., { en: 'en-US', am: 'am-ET' }
   */
  public getLocaleMap(): Record<string, string> {
    const defaultMap: Record<string, string> = {
      en: 'en-US',
      am: 'am-ET',
      om: 'om-ET',
      so: 'so-SO',
      ti: 'ti-ET',
    };

    const value = this.get('VITE_I18N_LOCALE_MAP') as string;
    if (!value) return defaultMap;

    try {
      const pairs = value.split(',');
      const result: Record<string, string> = {};
      for (const pair of pairs) {
        const [lang, locale] = pair.trim().split('=');
        if (lang && locale) {
          result[lang.trim()] = locale.trim();
        }
      }
      return Object.keys(result).length > 0 ? result : defaultMap;
    } catch {
      console.warn('Failed to parse VITE_I18N_LOCALE_MAP, using defaults');
      return defaultMap;
    }
  }

  /**
   * Gets currency formatting configuration.
   */
  public getCurrencyFormatConfig() {
    return {
      minDecimals: this.getNumber('VITE_I18N_CURRENCY_MIN_DECIMALS', 2),
      maxDecimals: this.getNumber('VITE_I18N_CURRENCY_MAX_DECIMALS', 2),
    };
  }

  /**
   * Gets complete i18n configuration object.
   * Convenience method that returns all i18n settings.
   */
  public getI18nConfig() {
    return {
      defaultLanguage: this.getDefaultLanguage(),
      fallbackLanguage: this.getFallbackLanguage(),
      defaultCurrency: this.getDefaultCurrency(),
      supportedLanguages: this.getSupportedLanguages(),
      localeMap: this.getLocaleMap(),
      currency: this.getCurrencyFormatConfig(),
    };
  }

  // PWA Configuration

  /**
   * Check if PWA is enabled.
   */
  public isPwaEnabled(): boolean {
    const value = this.get('VITE_PWA_ENABLED') as string | boolean;
    return value === 'true' || value === true;
  }

  /**
   * Check if service worker should be registered.
   */
  public shouldRegisterSw(): boolean {
    const value = this.get('VITE_PWA_REGISTER_SW');
    return value === 'true' || value === true;
  }

  /**
   * Gets the PWA app name.
   */
  public getPwaAppName(): string {
    return (this.get('VITE_PWA_APP_NAME') as string) || 'Student Portal';
  }

  /**
   * Gets the PWA short name (for home screen).
   */
  public getPwaShortName(): string {
    return (this.get('VITE_PWA_SHORT_NAME') as string) || 'Portal';
  }

  /**
   * Gets the PWA description.
   */
  public getPwaDescription(): string {
    return (this.get('VITE_PWA_DESCRIPTION') as string) || 'Student Information System';
  }

  /**
   * Gets the PWA display mode.
   * Options: standalone | fullscreen | minimal-ui | browser
   */
  public getPwaDisplay(): 'standalone' | 'fullscreen' | 'minimal-ui' | 'browser' {
    const value = this.get('VITE_PWA_DISPLAY') as string;
    const validModes = ['standalone', 'fullscreen', 'minimal-ui', 'browser'];
    return validModes.includes(value) ? (value as 'standalone' | 'fullscreen' | 'minimal-ui' | 'browser') : 'standalone';
  }

  /**
   * Gets the PWA start URL.
   */
  /**
   * Retrieves the CSRF configuration.
   * @returns CSRF cookie and header names.
   */
  public getCsrfConfig() {
    return this.frontendConfig?.api.csrf || {
      cookieName: 'csrf-token',
      headerName: 'X-CSRF-Token'
    };
  }

  public getPwaStartUrl(): string {
    return (this.get('VITE_PWA_START_URL') as string) || '/';
  }

  /**
   * Gets the PWA theme color.
   */
  public getPwaThemeColor(): string {
    return (this.get('VITE_PWA_THEME_COLOR') as string) || '#3b82f6';
  }

  /**
   * Gets the PWA background color.
   */
  public getPwaBackgroundColor(): string {
    return (this.get('VITE_PWA_BACKGROUND_COLOR') as string) || '#ffffff';
  }

  /**
   * Gets the offline message to display.
   */
  public getOfflineMessage(): string {
    return (this.get('VITE_OFFLINE_MESSAGE') as string) || 'You are currently offline.';
  }

  /**
   * Check if offline banner is dismissible.
   */
  public isOfflineBannerDismissible(): boolean {
    const value = this.get('VITE_OFFLINE_BANNER_DISMISSIBLE');
    return value === 'true' || value === true || value === undefined;
  }

  /**
   * Gets the timeout (ms) for clearing the "wasOffline" state after reconnection.
   */
  public getReconnectedTimeout(): number {
    return this.getNumber('VITE_OFFLINE_RECONNECTED_TIMEOUT_MS', 5000);
  }

  /**
   * Gets the localStorage key for offline sync queue.
   */
  public getOfflineStorageKey(): string {
    return (this.get('VITE_OFFLINE_STORAGE_KEY') as string) || 'offline_sync_queue';
  }

  /**
   * Gets the timeout (ms) for showing the "reconnected" banner.
   */
  public getReconnectedBannerTimeout(): number {
    return this.getNumber('VITE_OFFLINE_RECONNECTED_BANNER_TIMEOUT_MS', 3000);
  }

  /**
   * Gets complete PWA configuration object.
   * Convenience method that returns all PWA settings.
   */
  public getPwaConfig() {
    return {
      enabled: this.isPwaEnabled(),
      registerSw: this.shouldRegisterSw(),
      appName: this.getPwaAppName(),
      shortName: this.getPwaShortName(),
      description: this.getPwaDescription(),
      display: this.getPwaDisplay(),
      startUrl: this.getPwaStartUrl(),
      themeColor: this.getPwaThemeColor(),
      backgroundColor: this.getPwaBackgroundColor(),
      offline: {
        message: this.getOfflineMessage(),
        bannerDismissible: this.isOfflineBannerDismissible(),
      },
    };
  }


  // Financial Analytics Configuration
  /**
   * Gets financial analytics configuration for forecasting and chart visualization.
   * All values dynamically loaded from environment variables.
   */
  public getFinancialAnalyticsConfig() {
    return {
      // Forecast periods
      forecastMonthsAhead: this.getNumber('VITE_FINANCIAL_FORECAST_MONTHS_AHEAD', 6),
      cashFlowDaysAhead: this.getNumber('VITE_FINANCIAL_CASH_FLOW_DAYS_AHEAD', 90),

      // Chart colors - revenue
      chartRevenueColor: (this.get('VITE_FINANCIAL_CHART_REVENUE_COLOR') as string) || '#3B82F6',
      chartRevenueBg: (this.get('VITE_FINANCIAL_CHART_REVENUE_BG') as string) || 'rgba(59, 130, 246, 0.1)',

      // Chart colors - positive/negative flow
      chartPositiveColor: (this.get('VITE_FINANCIAL_CHART_POSITIVE_COLOR') as string) || '#22C55E',
      chartPositiveBg: (this.get('VITE_FINANCIAL_CHART_POSITIVE_BG') as string) || 'rgba(34, 197, 94, 0.6)',
      chartNegativeColor: (this.get('VITE_FINANCIAL_CHART_NEGATIVE_COLOR') as string) || '#EF4444',
      chartNegativeBg: (this.get('VITE_FINANCIAL_CHART_NEGATIVE_BG') as string) || 'rgba(239, 68, 68, 0.6)',
    };
  }


  // Feature Flags Configuration

  /**
   * Gets the default feature flag values.
   * Used as fallback when API is unavailable.
   */
  public getDefaultFeatureFlags(): Record<string, boolean> {
    const defaultFlags = this.get('VITE_FEATURE_FLAG_DEFAULTS');
    if (defaultFlags) {
      try {
        return JSON.parse(defaultFlags as string);
      } catch {
        console.warn('Invalid VITE_FEATURE_FLAG_DEFAULTS JSON, using empty defaults');
      }
    }
    // Hardcoded fallback only if env var is missing/invalid
    return {
      online_payments: true,
      scholarships: true,
      feedback_system: true,
    };
  }

  /**
   * Gets the feature flag refresh interval in milliseconds.
   */
  public getFeatureFlagRefreshInterval(): number {
    return this.getNumber('VITE_FEATURE_FLAG_REFRESH_INTERVAL_MS', 300000); // 5 minutes default
  }

  // ============================================================================
  // Forensic Audit Validation Configuration
  // ============================================================================

  /**
   * Gets forensic audit validation configuration for filter inputs and pagination.
   * 
   * Mirrors the backend's `WorkflowForensicConfigSchema` defaults, ensuring
   * identical validation constraints on both sides of the API boundary.
   * All values are dynamically loaded from environment variables.
   * 
   * @see apps/backend/src/config/config.schema.ts — WorkflowForensicConfigSchema
   * @see apps/backend/src/validations/workflowForensicValidations.ts
   */
  /**
   * Gets forensic audit validation configuration for filter inputs and pagination.
   * 
   * Mirrors the backend's `WorkflowForensicConfigSchema` defaults, ensuring
   * identical validation constraints on both sides of the API boundary.
   * All values are dynamically loaded from environment variables.
   * 
   * @see apps/backend/src/config/config.schema.ts — WorkflowForensicConfigSchema
   * @see apps/backend/src/validations/workflowForensicValidations.ts
   */
  public getForensicValidationConfig() {
    return {
      /** Mirrors backend config.maxFilterStringLength */
      maxFilterStringLength: this.getNumber('VITE_FORENSIC_MAX_FILTER_STRING_LENGTH', 128),
      /** Mirrors backend config.defaultPageSize */
      defaultPageSize: this.getNumber('VITE_FORENSIC_DEFAULT_PAGE_SIZE', 20),
      /** Mirrors backend config.maxPageSize */
      maxPageSize: this.getNumber('VITE_FORENSIC_MAX_PAGE_SIZE', 100),
      /** Mirrors backend config.minPageSize */
      minPageSize: this.getNumber('VITE_FORENSIC_MIN_PAGE_SIZE', 1),
    };
  }

  // ============================================================================
  // Security & Authentication Configuration (DYNAMIC)
  // ============================================================================

  /**
   * Gets patterns for security-sensitive API calls.
   * These requests are treated with higher caution (e.g., no automatic retries).
   */
  public getSecuritySensitivePatterns(): string[] {
    const fromConfig = this.frontendConfig?.security?.sensitivePatterns;
    if (fromConfig && Array.isArray(fromConfig) && fromConfig.length > 0) {
      return fromConfig;
    }

    const envValue = this.get('VITE_SECURITY_SENSITIVE_PATTERNS');
    if (envValue && typeof envValue === 'string') {
      try {
        return envValue.split(',').map(p => p.trim());
      } catch {
        console.warn('Failed to parse VITE_SECURITY_SENSITIVE_PATTERNS - using defaults');
      }
    }

    // Enterprise-Grade Internal Defaults
    return [
      '/auth/login',
      '/auth/register',
      '/auth/reset-password',
      '/auth/forgot-password',
      '/payments/initiate'
    ];
  }

  /**
   * Gets the centralized authentication route configuration.
   * Eliminates hardcoded strings in navigation and session management.
   */
  public getAuthRoutes() {
    return {
      login: (this.get('VITE_AUTH_ROUTE_LOGIN') as string) || '/login',
      dashboard: (this.get('VITE_AUTH_ROUTE_DASHBOARD') as string) || '/dashboard',
      refresh: (this.get('VITE_AUTH_ROUTE_REFRESH') as string) || '/auth/refresh',
      logout: (this.get('VITE_AUTH_ROUTE_LOGOUT') as string) || '/auth/logout'
    };
  }

  /**
   * Retrieves the dynamic, read-only list of allowed external redirect origins.
   * Follows a zero-trust model by prioritizing environment configuration 
   * and recursively locking (freezing) the resulting dataset to defend 
   * against potential runtime prototype pollution or client-side script memory injection.
   */
  public getAllowedRedirectOrigins(): readonly string[] {
    let origins: string[] = [];

    // 1. Attempt to resolve from immutable env variables injected at build/deployment
    const envValue = this.get('VITE_SECURITY_ALLOWED_REDIRECT_ORIGINS');
    if (envValue && typeof envValue === 'string') {
      try {
        origins = envValue.split(',').map((p: string) => p.trim()).filter(Boolean);
      } catch {
        console.warn('Failed to parse VITE_SECURITY_ALLOWED_REDIRECT_ORIGINS - using fallback default whitelist');
      }
    }

    // 2. Fail-safe defaults if environment variable is missing or malformed
    if (origins.length === 0) {
      origins = [
        'https://checkout.chapa.co',
        'https://api.telebirr.com'
      ];
    }

    // 3. Prevent subsequent runtime mutation of the whitelist arrays
    return Object.freeze(origins);
  }

  /**
   * Returns security configuration for session synchronization and sensitive patterns.
   * Established as the single source of truth for session hardening.
   */
  public getSecurityConfig() {
    return this.getFrontendConfig().security;
  }


}

export default ConfigurationService.getInstance();
